import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  CircleDot,
  ClipboardList,
  Earth,
  HeartHandshake,
  Home,
  LayoutDashboard,
  Menu,
  ShieldCheck,
  Syringe,
  TrendingUp,
  Wallet,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useUser } from '../../context/UserContext';
import { fetchScipQuery } from '../../components/api';
import authService from '../../services/authService';
import { fetchPowerBIFabricSqlData, fetchPowerBISemanticData } from '../../services/powerbiService';
import COInstance from '../../pages/COInstance';
import StockPerformance from '../../pages/StockPerformance';
import TicketSystem from '../../pages/TicketSystem';
import TrackingSystem from '../../pages/TrackingSystem';
import COFeedback from '../../pages/COFeedback';
import DataWarehouse from '../../pages/DataWarehouse';
import NewVaccineIntro, { getHpvIntroductionPlanCount } from '../../pages/NewVaccineIntro';
import DemandVaccineTrust from '../../pages/DemandVaccineTrust';
import Budgets from '../../pages/Budgets';
import Outbreaks from '../../pages/Outbreaks';
import ImprovementPlanningJourney from '../../pages/ImprovementPlanningJourney';
import SCIPToolDetail from '../../pages/SCIPToolDetail';
import ManagementKPI from '../../pages/ManagementKPI';
import PowerBIEmbed from '../../components/PowerBIEmbed';
import ColdChainInventoryGaps from '../../pages/ColdChainInventoryGaps';
import VCCM from '../../pages/vccm';
import { countActiveOutbreakCells, toOutbreakCountry } from '../../utils/outbreakMatching';
import './webV2Module.css';

const THRIVE_STOCK_WORKSPACE_ID = '7d441575-4aa9-4260-99dd-543f002c0340';
const STOCK_INFODACOT_DATASET_ID = '55d2265c-4dd8-4281-b248-c7db48034ba0';
const STOCK_INFODACOT_MEASURE_NAME = 'Reporting_Rate_%';
const STOCK_INFODACOT_STOCKOUT_STORES_MEASURE_NAME = process.env.REACT_APP_VCCM_STOCKOUT_STORES_MEASURE_NAME || 'Rpt_Target';
const STOCK_INFODACOT_DATE_TABLE = process.env.REACT_APP_VCCM_STOCK_DATE_TABLE || 'Z_report_Dates';
const STOCK_INFODACOT_DATE_COLUMN = process.env.REACT_APP_VCCM_STOCK_DATE_COLUMN || 'report_date';
const DRIVE_REPORT_ID = '012635a9-02e9-4d41-b23a-63f6498d861e';
const DRIVE_WORKSPACE_ID = '7d441575-4aa9-4260-99dd-543f002c0340';
const EVMA_REPORT_ID = '4376d6c0-6a5e-4f47-938b-6dcabbfa1a98';
const EVMA_WORKSPACE_ID = '7d441575-4aa9-4260-99dd-543f002c0340';
const GAVI_INVENTORY_TABLE = process.env.REACT_APP_GAVI_INVENTORY_TABLE_NAME || 'gavi_inventory_data';
const GAVI_FINANCE_UPDATE_TABLE_NAME = process.env.REACT_APP_GAVI_FINANCE_UPDATE_TABLE_NAME || 'GaviFinanceUpdate';
const GIDEON_NEWS_LIMIT = 20;
const GIDEON_NEWS_DAYS = 7;
const GIDEON_ALERT_SINCE_DAYS = 2;
const HOME_NIS_PLANNING_YEAR = new Date().getFullYear();

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};

const findColumnKey = (columns, names) => columns.find((col) => names.includes(normalizeColumn(col))) || null;

const daxTableRef = (tableName) => `'${String(tableName || '').replace(/'/g, "''")}'`;
const daxColumnRef = (tableName, columnName) => `${daxTableRef(tableName)}[${String(columnName || '').replace(/]/g, ']]')}]`;

const buildVccmStockLatestFilter = (dateTable, measureName) => {
  if (dateTable?.yearMonthColumn) {
    const yearMonthRef = daxColumnRef(dateTable.tableName, dateTable.yearMonthColumn);
    return `LASTNONBLANK(ALL(${yearMonthRef}), 'z_Measures'[${measureName}])`;
  }

  if (dateTable?.dateColumn && !dateTable?.preferYearMonth) {
    const dateRef = daxColumnRef(dateTable.tableName, dateTable.dateColumn);
    return `LASTNONBLANK(ALL(${dateRef}), 'z_Measures'[${measureName}])`;
  }

  const yearRef = daxColumnRef(dateTable.tableName, dateTable.yearColumn);
  const monthNoRef = daxColumnRef(dateTable.tableName, dateTable.monthNoColumn);
  const monthRef = daxColumnRef(dateTable.tableName, dateTable.monthColumn);
  return `TOPN(
              1,
              FILTER(
                ALL(${yearRef}, ${monthNoRef}, ${monthRef}),
                NOT ISBLANK('z_Measures'[${measureName}])
              ),
              ${yearRef},
              DESC,
              ${monthNoRef},
              DESC
            )`;
};

const buildVccmStockPeriodExpression = (dateTable, latestFilter) => {
  if (dateTable?.yearMonthColumn) {
    const yearMonthRef = daxColumnRef(dateTable.tableName, dateTable.yearMonthColumn);
    return `CALCULATE(FORMAT(DATE(INT(MAX(${yearMonthRef}) / 100), MAX(${yearMonthRef}) - INT(MAX(${yearMonthRef}) / 100) * 100, 1), "MMM yyyy"), ${latestFilter})`;
  }

  if (dateTable?.dateColumn && !dateTable?.preferYearMonth) {
    const dateRef = daxColumnRef(dateTable.tableName, dateTable.dateColumn);
    return `CALCULATE(FORMAT(MAX(${dateRef}), "MMM yyyy"), ${latestFilter})`;
  }

  const yearRef = daxColumnRef(dateTable.tableName, dateTable.yearColumn);
  const monthRef = daxColumnRef(dateTable.tableName, dateTable.monthColumn);
  return `CALCULATE(MAX(${monthRef}) & " " & FORMAT(MAX(${yearRef}), "0"), ${latestFilter})`;
};

const buildVccmStockMatrixDaxQuery = ({
  dateTable,
  countryIso3Ref,
  countryNameRef,
  gaviCountryRef,
  measureName,
  stockoutStoresMeasureName
}) => {
  if (!countryIso3Ref || !dateTable?.tableName) return '';

  const countryNameGroup = countryNameRef ? `,
              ${countryNameRef}` : '';
  const gaviCountryGroup = gaviCountryRef ? `,
              ${gaviCountryRef}` : '';
  const latestFilter = buildVccmStockLatestFilter(dateTable, measureName);
  const stockoutStoresSummary = stockoutStoresMeasureName
    ? `,
              "Stockout_Stores__",
              IGNORE(CALCULATE('z_Measures'[${stockoutStoresMeasureName}], ${latestFilter}))`
    : '';

  return `EVALUATE
          SELECTCOLUMNS(
            SUMMARIZECOLUMNS(
              ${countryIso3Ref}${countryNameGroup}${gaviCountryGroup},
              "Reporting_Rate__",
              IGNORE(CALCULATE('z_Measures'[${measureName}], ${latestFilter})),
              "Reporting_Period__",
              IGNORE(${buildVccmStockPeriodExpression(dateTable, latestFilter)})${stockoutStoresSummary}
            ),
            "iso3",
            ${countryIso3Ref},
            "countryName",
            ${countryNameRef || 'BLANK()'},
            "gaviCountry",
            ${gaviCountryRef || 'BLANK()'},
            "reportingRate",
            [Reporting_Rate__],
            "reportingPeriod",
            [Reporting_Period__]${stockoutStoresMeasureName ? `,
            "stockoutStores",
            [Stockout_Stores__]` : `,
            "stockoutStores",
            BLANK()`}
          )`;
};

const parseMetricNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatCoveragePercent = (value) => {
  const numericValue = parseMetricNumber(value);
  return numericValue === null ? 'N/A' : `${numericValue.toFixed(1)}%`;
};

const parseSemanticNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const normalizedValue = typeof value === 'string'
    ? value.replace(/[%,"\s]/g, '')
    : value;
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatSemanticPercent = (value) => {
  const numericValue = parseSemanticNumber(value);
  if (numericValue === null) return 'N/A';
  const percentValue = Math.abs(numericValue) <= 2 ? numericValue * 100 : numericValue;
  return `${percentValue.toFixed(1)}%`;
};

const formatCoverageNumber = (value) => {
  const numericValue = parseMetricNumber(value);
  return numericValue === null ? 'N/A' : Math.round(numericValue).toLocaleString();
};

const getCoverageStatusClass = (value) => {
  const numericValue = parseMetricNumber(value);
  if (numericValue === null) return 'is-empty';
  if (numericValue >= 90) return 'is-coverage-high';
  if (numericValue >= 80) return 'is-coverage-medium';
  return 'is-coverage-low';
};

const getCoverageStatusLabel = (value) => {
  const numericValue = parseMetricNumber(value);
  if (numericValue === null) return 'No data';
  if (numericValue >= 90) return 'On track';
  if (numericValue >= 80) return 'Watch';
  return 'Low';
};

const averageMetric = (items, field) => {
  const values = items
    .map((item) => parseMetricNumber(item?.[field]))
    .filter((value) => value !== null);
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const clampCoverageValue = (value) => {
  const numericValue = parseMetricNumber(value);
  if (numericValue === null) return null;
  return Math.max(0, Math.min(numericValue, 100));
};

const getCoverageMarkerStyle = (value) => {
  const clampedValue = clampCoverageValue(value);
  return clampedValue === null ? undefined : { left: `${clampedValue}%` };
};

const getCoverageRecordTooltip = (country, record) => {
  if (!record) return `${country?.countryName || 'Country'} - no data`;

  return [
    `${country?.countryName || record.country || 'Country'} - ${record.vaccine}`,
    `Year: ${record.year || 'N/A'}`,
    `WUENIC: ${formatCoveragePercent(record.wuenic)}`,
    `Previous revision: ${formatCoveragePercent(record.wuenicPreviousRevision)}`,
    `Administrative coverage: ${formatCoveragePercent(record.administrativeCoverage)}`,
    `Government estimate: ${formatCoveragePercent(record.governmentEstimate)}`,
    `Grade of confidence: ${record.gradeOfConfidence || 'N/A'}`,
    `Children vaccinated: ${formatCoverageNumber(record.childrenVaccinated)}`,
    `Children in target: ${formatCoverageNumber(record.childrenInTarget)}`,
    `Births UNPD: ${formatCoverageNumber(record.birthsUnpd)}`,
    `Surviving infants UNPD: ${formatCoverageNumber(record.survivingInfantsUnpd)}`,
    `Comment: ${record.comment || 'N/A'}`
  ].join('\n');
};

const findCoverageRecord = (country, vaccine) => country?.antigens?.find((record) => record.vaccine === vaccine) || null;
const HOME_COUNTRY_ALL_VALUE = '__all__';
const HOME_GAVI_FINANCING_YEAR = '2025';
const HOME_COVERAGE_TREND_YEARS = 10;
const WUENIC_COVERAGE_COLUMNS = `
            w.country,
            w.iso3,
            w.vaccine,
            w.year,
            w.wuenic,
            w.wuenic_previous_revision,
            w.grade_of_confidence,
            w.administrative_coverage,
            w.government_estimate,
            w.children_vaccinated,
            w.children_in_target,
            w.births_unpd,
            w.surviving_infants_unpd,
            w.comment`;

const buildIso3SqlFilter = (iso3Values, column = 'w.iso3') => {
  if (!Array.isArray(iso3Values) || iso3Values.length === 0) return '';
  return `AND ${column} IN (${iso3Values.map((iso3) => `'${String(iso3).replace(/'/g, "''")}'`).join(', ')})`;
};

const mergeCoverageRows = (...rowSets) => {
  const seen = new Set();
  const merged = [];

  rowSets.forEach((rows) => {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const key = [
        String(row?.iso3 || '').trim().toUpperCase(),
        String(row?.vaccine || '').trim().toUpperCase(),
        String(row?.year || '').trim()
      ].join('|');
      if (!key.replace(/\|/g, '') || seen.has(key)) return;
      seen.add(key);
      merged.push(row);
    });
  });

  return merged;
};

const parseBudgetNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(String(value).replace(/,/g, '').replace(/\s/g, '').replace(/[$€£]/g, ''));
  return Number.isFinite(amount) ? amount : null;
};

const formatBudgetCurrency = (value) => {
  const amount = parseBudgetNumber(value);
  if (amount === null) return 'N/A';
  if (Math.abs(amount) >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
};

const unwrapBudgetPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return [];
  const body = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  return Array.isArray(body?.rows) ? body.rows : [];
};

const getHomeGaviTotalAmount = (row) => {
  const total = parseBudgetNumber(row?.gavi_total);
  if (total !== null) return total;

  const transactions = Array.isArray(row?.gavi_transactions) ? row.gavi_transactions : [];
  return transactions.reduce((sum, transaction) => sum + (parseBudgetNumber(transaction?.amount) || 0), 0);
};

const formatSummaryPercent = (value) => {
  const numericValue = parseMetricNumber(value);
  return numericValue === null ? 'N/A' : `${numericValue.toFixed(1)}%`;
};

const formatBudgetDelta = (value) => {
  const amount = parseBudgetNumber(value);
  if (amount === null) return 'N/A';
  return `${amount > 0 ? '+' : ''}${formatBudgetCurrency(amount)}`;
};

const getLatestSeriesPoint = (series) => series?.[series.length - 1] || null;

const getSeriesYearRange = (series) => {
  if (!Array.isArray(series) || series.length === 0) return 'No years';
  const firstYear = series[0]?.year;
  const latestYear = series[series.length - 1]?.year;
  return firstYear === latestYear ? String(latestYear) : `${firstYear}-${latestYear}`;
};

const getSeriesTrend = (series, formatter) => {
  if (!Array.isArray(series) || series.length < 2) return null;
  const latest = series[series.length - 1];
  const previous = series[series.length - 2];
  const delta = parseMetricNumber(latest?.value) - parseMetricNumber(previous?.value);
  if (!Number.isFinite(delta)) return null;
  if (Math.abs(delta) < 0.1) return { className: 'is-stable', label: 'Stable' };
  return {
    className: delta > 0 ? 'is-increasing' : 'is-decreasing',
    label: formatter(delta)
  };
};

const createSummaryTooltip = (countryName, label, series, formatter) => {
  if (!Array.isArray(series) || series.length === 0) return `${countryName} - ${label}: no data`;
  return [
    `${countryName} - ${label}`,
    ...series.map((point) => `${point.year}: ${formatter(point.value)}`)
  ].join('\n');
};

const getVciSummaryScore = (row) => {
  const values = [
    row?.children,
    row?.safe,
    row?.effective,
    row?.beliefs,
    row?.trust_government,
    row?.trust_healthcare
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (values.length === 0) return null;
  return (values.reduce((sum, value) => sum + value, 0) / values.length) * 100;
};

const FINANCE_COUNTRY_ALIASES = {
  drc: 'democratic republic of the congo',
  'dr congo': 'democratic republic of the congo',
  'democratic republic of congo': 'democratic republic of the congo',
  'congo dem rep': 'democratic republic of the congo',
  'congo democratic republic of the': 'democratic republic of the congo',
  'congo republic': 'republic of the congo',
  'republic of congo': 'republic of the congo',
  congo: 'republic of the congo',
  'cote divoire': 'cote divoire',
  'ivory coast': 'cote divoire',
  'the gambia': 'gambia',
  'lao pdr': 'laos',
  'lao peoples democratic republic': 'laos',
  'united republic of tanzania': 'tanzania',
  'tanzania united republic of': 'tanzania',
  'syrian arab republic': 'syria',
  'sao tome and principe': 'sao tome and principe'
};

const normalizeFinanceCountryName = (value) => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
  return FINANCE_COUNTRY_ALIASES[normalized] || normalized;
};

const normalizeFinanceColumnName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/['’]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const findColumnKeyByPrefix = (columns, prefix) => {
  const wanted = normalizeFinanceColumnName(prefix);
  if (!wanted) return null;
  const normalizedColumns = columns.map((column) => ({
    column,
    normalized: normalizeFinanceColumnName(column)
  }));
  return normalizedColumns.find((item) => item.normalized === wanted)?.column
    || normalizedColumns.find((item) => item.normalized.startsWith(wanted))?.column
    || null;
};

const getYearFromText = (value) => {
  const text = String(value || '').trim();
  if (!text || text === '-' || text === '—') return null;
  const parsedDate = new Date(text);
  if (!Number.isNaN(parsedDate.getTime())) return parsedDate.getFullYear();
  const yearMatch = text.match(/\b(20\d{2})\b/);
  return yearMatch ? Number(yearMatch[1]) : null;
};

const MiniSparkline = ({ series, variant = 'line', maxValue = null }) => {
  if (!Array.isArray(series) || series.length === 0) {
    return <span className="summary-sparkline-empty" aria-hidden="true" />;
  }

  const width = 112;
  const height = 36;
  const padding = 4;
  const values = series.map((point) => parseMetricNumber(point.value)).filter((value) => value !== null);
  const minValue = maxValue !== null ? 0 : Math.min(...values);
  const rawMaxValue = maxValue !== null ? maxValue : Math.max(...values);
  const span = rawMaxValue - minValue || 1;

  if (variant === 'bars') {
    const barGap = 3;
    const barWidth = Math.max(4, (width - padding * 2 - barGap * (series.length - 1)) / series.length);
    return (
      <svg className="summary-sparkline-svg is-bars" viewBox={`0 0 ${width} ${height}`} role="img" aria-hidden="true">
        {series.map((point, index) => {
          const value = parseMetricNumber(point.value) || 0;
          const barHeight = Math.max(2, ((value - minValue) / span) * (height - padding * 2));
          const x = padding + index * (barWidth + barGap);
          const y = height - padding - barHeight;
          return <rect key={`${point.year}-${index}`} x={x} y={y} width={barWidth} height={barHeight} rx="2" />;
        })}
      </svg>
    );
  }

  const points = series.map((point, index) => {
    const value = parseMetricNumber(point.value) || 0;
    const x = series.length === 1
      ? width / 2
      : padding + (index / (series.length - 1)) * (width - padding * 2);
    const y = height - padding - ((value - minValue) / span) * (height - padding * 2);
    return { x, y, key: `${point.year}-${index}` };
  });

  return (
    <svg className="summary-sparkline-svg is-line" viewBox={`0 0 ${width} ${height}`} role="img" aria-hidden="true">
      {points.length > 1 && <polyline points={points.map((point) => `${point.x},${point.y}`).join(' ')} />}
      {points.map((point) => <circle key={point.key} cx={point.x} cy={point.y} r="2.4" />)}
    </svg>
  );
};

const SummaryDomainCell = ({ countryName, label, series, formatter, trendFormatter, variant = 'line', maxValue = null }) => {
  const latest = getLatestSeriesPoint(series);
  if (!latest) {
    return <span className="summary-domain-empty">No data</span>;
  }

  const trend = getSeriesTrend(series, trendFormatter || formatter);
  return (
    <div className="summary-domain-cell" title={createSummaryTooltip(countryName, label, series, formatter)}>
      <div className="summary-domain-main">
        <MiniSparkline series={series} variant={variant} maxValue={maxValue} />
        <span className="summary-domain-value">{formatter(latest.value)}</span>
      </div>
      <div className="summary-domain-meta">
        <span>{getSeriesYearRange(series)}</span>
        {trend && <span className={`summary-domain-trend ${trend.className}`}>{trend.label}</span>}
      </div>
    </div>
  );
};

function WebV2Module({ initialPlaceholderPage = null, initialThriveSubpage = null } = {}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(true);
  const [activeThriveSubpage, setActiveThriveSubpage] = useState(initialThriveSubpage);
  const [activePlaceholderPage, setActivePlaceholderPage] = useState(initialPlaceholderPage);
  const { user } = useUser();
  const [targetPopulation, setTargetPopulation] = useState(null);
  const [dtp3Coverage, setDtp3Coverage] = useState(null);
  const [isTargetPopulationLoading, setIsTargetPopulationLoading] = useState(true);
  const [coverageYearRows, setCoverageYearRows] = useState([]);
  const [coverageTrendRows, setCoverageTrendRows] = useState([]);
  const [coverageYearOptions, setCoverageYearOptions] = useState([]);
  const [isCoverageYearLoading, setIsCoverageYearLoading] = useState(true);
  const [isCoverageTrendLoading, setIsCoverageTrendLoading] = useState(true);
  const [selectedCoverageCountryKey, setSelectedCoverageCountryKey] = useState('');
  const [selectedCoverageYear, setSelectedCoverageYear] = useState('');
  const [coverageCountryTypeFilter, setCoverageCountryTypeFilter] = useState('gavi');
  const [showAllCoverageCountries, setShowAllCoverageCountries] = useState(false);
  const [showAllSummaryCountries, setShowAllSummaryCountries] = useState(false);
  const [selectedHomeCountryIso3, setSelectedHomeCountryIso3] = useState(HOME_COUNTRY_ALL_VALUE);
  const [homeGaviFinancing, setHomeGaviFinancing] = useState(null);
  const [homeWorkingEquipmentRate, setHomeWorkingEquipmentRate] = useState(null);
  const [isHomeWorkingEquipmentLoading, setIsHomeWorkingEquipmentLoading] = useState(true);
  const [homeStockAvailabilityRate, setHomeStockAvailabilityRate] = useState(null);
  const [isHomeStockAvailabilityLoading, setIsHomeStockAvailabilityLoading] = useState(true);
  const [vccmStockMetricRows, setVccmStockMetricRows] = useState([]);
  const [vccmEvmaMetricRows, setVccmEvmaMetricRows] = useState([]);
  const [vccmTssRows, setVccmTssRows] = useState([]);
  const [homeBudgetRows, setHomeBudgetRows] = useState([]);
  const [isHomeGaviFinancingLoading, setIsHomeGaviFinancingLoading] = useState(true);
  const [isVccmStockMetricsLoading, setIsVccmStockMetricsLoading] = useState(true);
  const [isVccmEvmaMetricsLoading, setIsVccmEvmaMetricsLoading] = useState(true);
  const [isVccmTssLoading, setIsVccmTssLoading] = useState(true);
  const [homeGaviCountryRows, setHomeGaviCountryRows] = useState([]);
  const [isHomeGaviCountryLoading, setIsHomeGaviCountryLoading] = useState(true);
  const [summaryCountryTypeFilter, setSummaryCountryTypeFilter] = useState('gavi');
  const [homeVciRows, setHomeVciRows] = useState([]);
  const [isHomeVciLoading, setIsHomeVciLoading] = useState(true);
  const [taRows, setTaRows] = useState([]);
  const [taColumns, setTaColumns] = useState([]);
  const [isTaLoading, setIsTaLoading] = useState(true);
  const [homeGideonAlertItems, setHomeGideonAlertItems] = useState([]);
  const [homeOutbreakCountries, setHomeOutbreakCountries] = useState([]);
  const [homeGideonOutbreakError, setHomeGideonOutbreakError] = useState(false);
  const [isHomeGideonOutbreaksLoading, setIsHomeGideonOutbreaksLoading] = useState(true);
  const [homeNisPlannedCount, setHomeNisPlannedCount] = useState(null);
  const [isHomeNisPlannedLoading, setIsHomeNisPlannedLoading] = useState(true);
  const [whoArticles, setWhoArticles] = useState([]);
  const [isWhoArticlesLoading, setIsWhoArticlesLoading] = useState(true);

  const displayName = `${user?.name || ''} ${user?.lastName || ''}`.trim() || 'User';
  const avatarInitials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';
  const allowedCountries = Array.isArray(user?.allowedCountries) ? user.allowedCountries.filter(Boolean) : [];
  const countryAccess = allowedCountries.length > 1 ? 'Multiple' : (allowedCountries[0] || user?.country || 'Not set');
  const roleLabel = user?.accessLevel || 'User';
  const normalizedAccessLevel = String(user?.accessLevel || '').toLowerCase();
  const dynamicContextTitle = normalizedAccessLevel === 'regional'
    ? 'Regional'
    : normalizedAccessLevel === 'country' || normalizedAccessLevel === 'country-admin'
      ? countryAccess
      : 'Global';
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
  const whoNewsApiUrl = 'https://uni-func-pd-thrive360-dev.azurewebsites.net/api/scrape-who?limit=3';

  const allowedIso3Values = useMemo(() => {
    const fromAllowed = Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3 : [];
    const fromCountry = user?.country_iso3 ? [user.country_iso3] : [];
    return [...fromAllowed, ...fromCountry]
      .map((value) => String(value || '').trim().toUpperCase())
      .filter(Boolean)
      .filter((value, idx, arr) => arr.indexOf(value) === idx);
  }, [user?.allowedCountriesIso3, user?.country_iso3]);
  const allowedCountryValues = useMemo(() => {
    const fromAllowed = Array.isArray(user?.allowedCountries) ? user.allowedCountries : [];
    const fromCountry = user?.country ? [user.country] : [];
    return [...fromAllowed, ...fromCountry]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .filter((value) => value.toLowerCase() !== 'global' && value.toLowerCase() !== 'all')
      .filter((value, idx, arr) => arr.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === idx);
  }, [user?.allowedCountries, user?.country]);
  const homeCountryOptions = useMemo(() => {
    const options = [];
    const allowedCountryNames = Array.isArray(user?.allowedCountries) ? user.allowedCountries : [];
    const allowedCountryIso3s = Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3 : [];
    const registerCountry = (name, iso3) => {
      const value = String(iso3 || '').trim().toUpperCase();
      const label = String(name || value || '').trim();
      if (!value || options.some((option) => option.value === value)) return;
      options.push({ value, label: label || value });
    };

    allowedCountryNames.forEach((name, index) => registerCountry(name, allowedCountryIso3s[index]));
    allowedCountryIso3s.forEach((iso3, index) => registerCountry(allowedCountryNames[index], iso3));
    registerCountry(user?.country, user?.country_iso3);

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [user?.allowedCountries, user?.allowedCountriesIso3, user?.country, user?.country_iso3]);
  const selectedHomeCountryOption = useMemo(
    () => homeCountryOptions.find((option) => option.value === selectedHomeCountryIso3) || null,
    [homeCountryOptions, selectedHomeCountryIso3]
  );
  const selectedHomeCountryLabel = selectedHomeCountryIso3 === HOME_COUNTRY_ALL_VALUE
    ? dynamicContextTitle
    : selectedHomeCountryOption?.label || dynamicContextTitle;
  const homeCountryFilterIso3Values = useMemo(() => {
    const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
    if (selectedHomeCountryIso3 !== HOME_COUNTRY_ALL_VALUE) return [selectedHomeCountryIso3].filter(Boolean);
    return isGlobalScope ? [] : allowedIso3Values;
  }, [allowedIso3Values, normalizedAccessLevel, selectedHomeCountryIso3]);
  const homeCountryFilterNames = useMemo(() => {
    if (homeCountryFilterIso3Values.length === 0) return [];
    const iso3Filter = new Set(homeCountryFilterIso3Values.map((iso3) => String(iso3 || '').trim().toUpperCase()));
    return homeCountryOptions
      .filter((option) => iso3Filter.has(String(option.value || '').trim().toUpperCase()))
      .map((option) => option.label)
      .filter(Boolean);
  }, [homeCountryFilterIso3Values, homeCountryOptions]);

  useEffect(() => {
    if (homeCountryOptions.length === 0) {
      if (selectedHomeCountryIso3 !== HOME_COUNTRY_ALL_VALUE) setSelectedHomeCountryIso3(HOME_COUNTRY_ALL_VALUE);
      return;
    }

    if (homeCountryOptions.length === 1) {
      const onlyCountryValue = homeCountryOptions[0].value;
      if (selectedHomeCountryIso3 !== onlyCountryValue) setSelectedHomeCountryIso3(onlyCountryValue);
      return;
    }

    if (
      selectedHomeCountryIso3 !== HOME_COUNTRY_ALL_VALUE &&
      !homeCountryOptions.some((option) => option.value === selectedHomeCountryIso3)
    ) {
      setSelectedHomeCountryIso3(HOME_COUNTRY_ALL_VALUE);
    }
  }, [homeCountryOptions, selectedHomeCountryIso3]);

  useEffect(() => {
    let isMounted = true;

    const loadTargetPopulation = async () => {
      setIsTargetPopulationLoading(true);
      try {
        const idToken = await authService.getIdToken();

        const query = `
          SELECT
            SUM(
              CASE
                WHEN indicator = 'DTP3 (2024) - Children in Target' THEN CAST(
                  NULLIF(
                    REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                    ''
                  ) AS DECIMAL(20, 3)
                )
                ELSE 0
              END
            ) AS target_population,
            AVG(
              CASE
                WHEN indicator = 'DTP3 (2024) - WUENIC' THEN CAST(
                  NULLIF(
                    REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(value, ''), ',', ''), ' ', ''), '%', ''), '"', ''),
                    ''
                  ) AS DECIMAL(20, 3)
                )
                ELSE NULL
              END
            ) AS dtp3_coverage
          FROM summarized_indicators
          WHERE table_name = 'wuenic_data_coverage'
            AND indicator IN (
              'DTP3 (2024) - Children in Target',
              'DTP3 (2024) - WUENIC'
            )
            ${homeCountryFilterIso3Values.length > 0
              ? `AND iso3 IN (${homeCountryFilterIso3Values.map((iso3) => `'${iso3.replace(/'/g, "''")}'`).join(', ')})`
              : ''}
          ;
        `;

        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({ query }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Target population request failed (${response.status})`);
        }

        const payload = await response.json();
        const resultRows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
        const targetPopulationValue = resultRows?.[0]?.target_population;
        const dtp3CoverageValue = resultRows?.[0]?.dtp3_coverage;

        if (!isMounted) return;
        const numericTargetPopulation = targetPopulationValue === null || targetPopulationValue === undefined || targetPopulationValue === ''
          ? null
          : Number(targetPopulationValue);
        const numericDtp3Coverage = dtp3CoverageValue === null || dtp3CoverageValue === undefined || dtp3CoverageValue === ''
          ? null
          : Number(dtp3CoverageValue);
        setTargetPopulation(Number.isFinite(numericTargetPopulation) ? numericTargetPopulation : null);
        setDtp3Coverage(Number.isFinite(numericDtp3Coverage) ? numericDtp3Coverage : null);
      } catch (error) {
        if (!isMounted) return;
        setTargetPopulation(null);
        setDtp3Coverage(null);
      } finally {
        if (isMounted) setIsTargetPopulationLoading(false);
      }
    };

    loadTargetPopulation();

    return () => {
      isMounted = false;
    };
  }, [apiBase, homeCountryFilterIso3Values]);

  const targetPopulationDisplay = isTargetPopulationLoading
    ? 'Loading...'
    : targetPopulation === null
      ? 'N/A'
      : Math.round(targetPopulation).toLocaleString();
  const dtp3CoverageDisplay = isTargetPopulationLoading
    ? 'Loading...'
    : dtp3Coverage === null
      ? 'N/A'
      : `${dtp3Coverage.toFixed(1)}%`;
  const homeGaviFinancingDisplay = isHomeGaviFinancingLoading ? 'Loading...' : formatBudgetCurrency(homeGaviFinancing);
  const homeNewVaccineIntroDisplay = getHpvIntroductionPlanCount(
    homeCountryFilterIso3Values.length > 0 ? homeCountryFilterNames : undefined
  ).toLocaleString();
  const homeWorkingEquipmentDisplay = isHomeWorkingEquipmentLoading
    ? 'Loading...'
    : formatSemanticPercent(homeWorkingEquipmentRate);
  const homeStockAvailabilityDisplay = isHomeStockAvailabilityLoading
    ? 'Loading...'
    : formatSemanticPercent(homeStockAvailabilityRate);

  useEffect(() => {
    let isMounted = true;

    const loadHomeWorkingEquipmentRate = async () => {
      setIsHomeWorkingEquipmentLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const countryFilter = homeCountryFilterIso3Values.length > 0
          ? `AND iso3 IN (${homeCountryFilterIso3Values.map((iso3) => `'${iso3.replace(/'/g, "''")}'`).join(', ')})`
          : '';
        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT
                COUNT(*) AS equipment_count,
                SUM(CASE WHEN LOWER(TRIM(operational_status)) = 'functional' THEN 1 ELSE 0 END) AS functional_equipment_count
              FROM ${GAVI_INVENTORY_TABLE}
              WHERE iso3 IS NOT NULL
                AND TRIM(iso3) <> ''
                ${countryFilter};
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Working equipment request failed (${response.status})`);
        }

        const payload = await response.json();
        const resultRows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
        const equipmentCount = parseMetricNumber(resultRows?.[0]?.equipment_count);
        const functionalEquipmentCount = parseMetricNumber(resultRows?.[0]?.functional_equipment_count);
        const workingEquipmentRate = equipmentCount && equipmentCount > 0 && functionalEquipmentCount !== null
          ? functionalEquipmentCount / equipmentCount
          : null;

        if (!isMounted) return;
        setHomeWorkingEquipmentRate(workingEquipmentRate);
      } catch (error) {
        if (!isMounted) return;
        setHomeWorkingEquipmentRate(null);
      } finally {
        if (isMounted) setIsHomeWorkingEquipmentLoading(false);
      }
    };

    loadHomeWorkingEquipmentRate();

    return () => {
      isMounted = false;
    };
  }, [apiBase, homeCountryFilterIso3Values]);

  useEffect(() => {
    let isMounted = true;

    const loadHomeStockAvailability = async () => {
      setIsHomeStockAvailabilityLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const countryFilter = homeCountryFilterIso3Values.length > 0
          ? `AND v.iso3 IN (${homeCountryFilterIso3Values.map((iso3) => `'${iso3.replace(/'/g, "''")}'`).join(', ')})`
          : '';
        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT AVG(v.Rolling_6_Month_Availability) AS avg_rolling_availability
              FROM thrive_appdb_prd.vw_district_stock_availability_6m v
              INNER JOIN (
                SELECT iso3, MAX(Report_Month) AS Report_Month
                FROM thrive_appdb_prd.vw_district_stock_availability_6m
                GROUP BY iso3
              ) latest
                ON latest.iso3 = v.iso3
               AND latest.Report_Month = v.Report_Month
              WHERE v.iso3 IS NOT NULL
                AND TRIM(v.iso3) <> ''
                ${countryFilter};
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Stock availability request failed (${response.status})`);
        }

        const payload = await response.json();
        const resultRows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
        const averageAvailability = parseMetricNumber(
          resultRows?.[0]?.avg_rolling_availability ?? resultRows?.[0]?.AVG_ROLLING_AVAILABILITY
        );

        if (!isMounted) return;
        setHomeStockAvailabilityRate(averageAvailability);
      } catch (error) {
        if (!isMounted) return;
        setHomeStockAvailabilityRate(null);
      } finally {
        if (isMounted) setIsHomeStockAvailabilityLoading(false);
      }
    };

    loadHomeStockAvailability();

    return () => {
      isMounted = false;
    };
  }, [apiBase, homeCountryFilterIso3Values]);

  useEffect(() => {
    let isMounted = true;

    const loadVccmStockMetrics = async () => {
      setIsVccmStockMetricsLoading(true);
      try {
        const stockMatrixDaxQueryOverride = String(process.env.REACT_APP_VCCM_STOCK_MATRIX_DAX_QUERY || '').trim();
        const countryTable = String(process.env.REACT_APP_VCCM_STOCK_COUNTRY_TABLE || 'z_Geo_admin').trim();
        const countryIso3Column = String(process.env.REACT_APP_VCCM_STOCK_COUNTRY_ISO3_COLUMN || 'iso3').trim();
        const countryNameColumn = String(process.env.REACT_APP_VCCM_STOCK_COUNTRY_NAME_COLUMN || 'Country_Name').trim();
        const gaviCountryColumn = String(process.env.REACT_APP_VCCM_STOCK_GAVI_COUNTRY_COLUMN || 'Gavi_Country').trim();
        const countryIso3Ref = countryTable && countryIso3Column
          ? `'${countryTable.replace(/'/g, "''")}'[${countryIso3Column.replace(/]/g, ']]')}]`
          : '';
        const countryNameRef = countryTable && countryNameColumn
          ? `'${countryTable.replace(/'/g, "''")}'[${countryNameColumn.replace(/]/g, ']]')}]`
          : '';
        const gaviCountryRef = countryTable && gaviCountryColumn
          ? `'${countryTable.replace(/'/g, "''")}'[${gaviCountryColumn.replace(/]/g, ']]')}]`
          : '';
        const stockoutStoresMeasureName = String(STOCK_INFODACOT_STOCKOUT_STORES_MEASURE_NAME || '').trim();

        const dateTable = stockMatrixDaxQueryOverride ? null : {
          tableName: String(STOCK_INFODACOT_DATE_TABLE || '').trim(),
          dateColumn: String(STOCK_INFODACOT_DATE_COLUMN || '').trim()
        };
        const stockMatrixDaxQuery = stockMatrixDaxQueryOverride || buildVccmStockMatrixDaxQuery({
          dateTable,
          countryIso3Ref,
          countryNameRef,
          gaviCountryRef,
          measureName: STOCK_INFODACOT_MEASURE_NAME,
          stockoutStoresMeasureName
        });

        if (!stockMatrixDaxQuery) {
          if (!isMounted) return;
          setVccmStockMetricRows([]);
          return;
        }

        const data = await fetchPowerBISemanticData(
          STOCK_INFODACOT_DATASET_ID,
          THRIVE_STOCK_WORKSPACE_ID,
          stockMatrixDaxQuery,
          5000
        );

        if (!isMounted) return;
        setVccmStockMetricRows(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        if (!isMounted) return;
        setVccmStockMetricRows([]);
      } finally {
        if (isMounted) setIsVccmStockMetricsLoading(false);
      }
    };

    loadVccmStockMetrics();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadVccmEvmaMetrics = async () => {
      setIsVccmEvmaMetricsLoading(true);
      try {
        const gaviFinanceDatasetId = process.env.REACT_APP_GAVI_FINANCE_DATASET_ID || 'e1a596ea-23a5-495e-b5f4-faaa557bbe77';
        const gaviFinanceWorkspaceId = process.env.REACT_APP_GAVI_FINANCE_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
        const gaviFinanceDaxQuery = process.env.REACT_APP_GAVI_FINANCE_DAX_QUERY || '';
        const gaviFinanceTableName = process.env.REACT_APP_GAVI_FINANCE_TABLE_NAME || 'NISCostTableSharepoint';
        const evmProcessQuery = `
          SELECT
            CM.Name AS CountryName,
            CM.IsoCode AS ISO3,
            CEPT.ProjectedAssessmentStartDate
          FROM SCIP.CountryEvmProcessTracker CEPT
          LEFT JOIN SCIP.CountryMaster CM ON CM.Id = CEPT.CountryId
          ORDER BY CEPT.LatestAssessmentEndDate DESC, CEPT.UpdatedDate DESC;
        `;

        const [evmProcessData, gaviData] = await Promise.all([
          fetchScipQuery(evmProcessQuery, {}),
          fetchPowerBISemanticData(
            gaviFinanceDatasetId,
            gaviFinanceWorkspaceId,
            String(gaviFinanceDaxQuery || '').trim() || undefined,
            5000,
            String(gaviFinanceDaxQuery || '').trim() ? undefined : (String(gaviFinanceTableName || '').trim() || undefined)
          )
        ]);

        const gaviColumns = Array.isArray(gaviData?.columns) ? gaviData.columns : [];
        const gaviCountryColumnKey = findColumnKey(gaviColumns, ['country']);
        const gaviEvmScoreColumnKey = findColumnKey(gaviColumns, ['evm composite', 'evm copmposite', 'evm_composite']);
        const evmProcessByCountry = new Map();

        (Array.isArray(evmProcessData) ? evmProcessData : []).forEach((row) => {
          const iso3 = String(row?.ISO3 || row?.IsoCode || '').trim().toUpperCase();
          const countryName = String(row?.CountryName || row?.Country || '').trim().toLowerCase();
          const key = iso3 || countryName;
          if (!key || evmProcessByCountry.has(key)) return;
          evmProcessByCountry.set(key, row);
        });

        const evmaRows = [];
        (Array.isArray(gaviData?.data) ? gaviData.data : []).forEach((row) => {
          const countryName = gaviCountryColumnKey ? String(row?.[gaviCountryColumnKey] || '').trim() : '';
          if (!countryName) return;
          const evmProcess = evmProcessByCountry.get(countryName.toLowerCase()) || null;
          evmaRows.push({
            countryName,
            evmScore: gaviEvmScoreColumnKey ? row?.[gaviEvmScoreColumnKey] : null,
            nextAssessmentDate: evmProcess?.ProjectedAssessmentStartDate || null
          });
        });

        evmProcessByCountry.forEach((row) => {
          const iso3 = String(row?.ISO3 || row?.IsoCode || '').trim().toUpperCase();
          const countryName = String(row?.CountryName || row?.Country || '').trim();
          if (!iso3 || evmaRows.some((item) => String(item.countryName || '').trim().toLowerCase() === countryName.toLowerCase())) return;
          evmaRows.push({
            iso3,
            countryName,
            evmScore: null,
            nextAssessmentDate: row?.ProjectedAssessmentStartDate || null
          });
        });

        if (!isMounted) return;
        setVccmEvmaMetricRows(evmaRows);
      } catch (error) {
        if (!isMounted) return;
        setVccmEvmaMetricRows([]);
      } finally {
        if (isMounted) setIsVccmEvmaMetricsLoading(false);
      }
    };

    loadVccmEvmaMetrics();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadVccmTssMappings = async () => {
      setIsVccmTssLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT
                country,
                iso3,
                vax_lmis,
                vax_tss,
                other_lmis,
                other_donor,
                other_tss,
                dhis2_natl
              FROM elmis_country_mapping;
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`eLMIS mapping request failed (${response.status})`);
        }

        const payload = await response.json();
        if (!isMounted) return;
        setVccmTssRows(Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []));
      } catch (error) {
        if (!isMounted) return;
        setVccmTssRows([]);
      } finally {
        if (isMounted) setIsVccmTssLoading(false);
      }
    };

    loadVccmTssMappings();

    return () => {
      isMounted = false;
    };
  }, [apiBase]);

  useEffect(() => {
    let isMounted = true;

    const loadHomeGaviFinancing = async () => {
      setIsHomeGaviFinancingLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const response = await fetch(`${apiBase}/gibd-budgets`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`
          },
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Budget request failed (${response.status})`);
        }

        const payload = await response.json();
        const budgetRows = unwrapBudgetPayload(payload);
        const allowedIso3Set = new Set(homeCountryFilterIso3Values.map((iso3) => iso3.toUpperCase()));
        const accessIso3Set = new Set(allowedIso3Values.map((iso3) => iso3.toUpperCase()));
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
        const summaryBudgetRows = budgetRows.filter((row) => {
          if (isGlobalScope || accessIso3Set.size === 0) return true;
          return accessIso3Set.has(String(row?.country_iso3 || '').trim().toUpperCase());
        });
        const total = budgetRows
          .filter((row) => String(row?.year || '').trim() === HOME_GAVI_FINANCING_YEAR)
          .filter((row) => {
            if (allowedIso3Set.size === 0) return true;
            return allowedIso3Set.has(String(row?.country_iso3 || '').trim().toUpperCase());
          })
          .reduce((sum, row) => sum + getHomeGaviTotalAmount(row), 0);

        if (!isMounted) return;
        setHomeGaviFinancing(total);
        setHomeBudgetRows(summaryBudgetRows);
      } catch (error) {
        if (!isMounted) return;
        setHomeGaviFinancing(null);
        setHomeBudgetRows([]);
      } finally {
        if (isMounted) setIsHomeGaviFinancingLoading(false);
      }
    };

    loadHomeGaviFinancing();

    return () => {
      isMounted = false;
    };
  }, [allowedIso3Values, apiBase, homeCountryFilterIso3Values, normalizedAccessLevel]);

  useEffect(() => {
    let isMounted = true;

    const loadHomeVciRows = async () => {
      setIsHomeVciLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
        const countryFilter = !isGlobalScope && allowedIso3Values.length > 0
          ? `AND v.iso3 IN (${allowedIso3Values.map((iso3) => `'${iso3.replace(/'/g, "''")}'`).join(', ')})`
          : '';

        const query = `
          SELECT
            v.country,
            v.iso3,
            v.year,
            v.children,
            v.safe,
            v.effective,
            v.beliefs,
            v.government AS trust_government,
            v.healthcare_system AS trust_healthcare
          FROM vci_data v
          WHERE v.iso3 IS NOT NULL
            AND TRIM(v.iso3) <> ''
            ${countryFilter}
          ORDER BY v.country, v.year ASC;
        `;

        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({ query }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`VCI summary request failed (${response.status})`);
        }

        const payload = await response.json();
        const resultRows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);

        if (!isMounted) return;
        setHomeVciRows(resultRows);
      } catch (error) {
        if (!isMounted) return;
        setHomeVciRows([]);
      } finally {
        if (isMounted) setIsHomeVciLoading(false);
      }
    };

    loadHomeVciRows();

    return () => {
      isMounted = false;
    };
  }, [allowedIso3Values, apiBase, normalizedAccessLevel]);

  useEffect(() => {
    let isMounted = true;

    const loadHomeGaviCountryRows = async () => {
      setIsHomeGaviCountryLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
        const countryFilter = !isGlobalScope && allowedIso3Values.length > 0
          ? `AND iso3 IN (${allowedIso3Values.map((iso3) => `'${iso3.replace(/'/g, "''")}'`).join(', ')})`
          : '';

        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT iso3, UNICEF_RO, Gavi_Country
              FROM geo_admin
              WHERE iso3 IS NOT NULL
                AND TRIM(iso3) <> ''
                ${countryFilter}
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Gavi country lookup failed (${response.status})`);
        }

        const payload = await response.json();
        const resultRows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);

        if (!isMounted) return;
        setHomeGaviCountryRows(resultRows);
      } catch (error) {
        if (!isMounted) return;
        setHomeGaviCountryRows([]);
      } finally {
        if (isMounted) setIsHomeGaviCountryLoading(false);
      }
    };

    loadHomeGaviCountryRows();

    return () => {
      isMounted = false;
    };
  }, [allowedIso3Values, apiBase, normalizedAccessLevel]);

  useEffect(() => {
    let mounted = true;

    const loadCoverageTrend = async () => {
      setIsCoverageTrendLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
        const countryFilter = isGlobalScope ? '' : buildIso3SqlFilter(allowedIso3Values);

        const yearsQuery = `
          SELECT DISTINCT w.year
          FROM thrive_appdb_prd.wuenic_data_coverage w
          WHERE w.year IS NOT NULL
            ${countryFilter}
          ORDER BY w.year DESC;
        `;
        const trendQuery = `
          SELECT
            ${WUENIC_COVERAGE_COLUMNS}
          FROM thrive_appdb_prd.wuenic_data_coverage w
          WHERE w.iso3 IS NOT NULL
            AND TRIM(w.iso3) <> ''
            AND UPPER(TRIM(w.vaccine)) IN ('DTP1', 'DTP3')
            AND w.year >= (
              SELECT MAX(year) - ${HOME_COVERAGE_TREND_YEARS - 1}
              FROM thrive_appdb_prd.wuenic_data_coverage
            )
            ${countryFilter}
          ORDER BY w.country, w.vaccine, w.year DESC;
        `;

        const [yearsResponse, trendResponse] = await Promise.all([
          fetch(`${apiBase}/db-query`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`
            },
            body: JSON.stringify({ query: yearsQuery }),
            mode: 'cors'
          }),
          fetch(`${apiBase}/db-query`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`
            },
            body: JSON.stringify({ query: trendQuery }),
            mode: 'cors'
          })
        ]);

        if (!yearsResponse.ok) {
          throw new Error(`Coverage year request failed (${yearsResponse.status})`);
        }
        if (!trendResponse.ok) {
          throw new Error(`Coverage trend request failed (${trendResponse.status})`);
        }

        const yearsPayload = await yearsResponse.json();
        const trendPayload = await trendResponse.json();
        const yearRows = Array.isArray(yearsPayload?.result) ? yearsPayload.result : (Array.isArray(yearsPayload) ? yearsPayload : []);
        const resultRows = Array.isArray(trendPayload?.result) ? trendPayload.result : (Array.isArray(trendPayload) ? trendPayload : []);
        const years = yearRows
          .map((row) => parseMetricNumber(row?.year))
          .filter((year) => year !== null)
          .sort((a, b) => b - a)
          .map((year) => String(year));

        if (!mounted) return;
        setCoverageYearOptions(years);
        setCoverageTrendRows(resultRows);
      } catch (error) {
        if (!mounted) return;
        setCoverageYearOptions([]);
        setCoverageTrendRows([]);
      } finally {
        if (mounted) setIsCoverageTrendLoading(false);
      }
    };

    loadCoverageTrend();

    return () => {
      mounted = false;
    };
  }, [allowedIso3Values, apiBase, normalizedAccessLevel]);

  useEffect(() => {
    let mounted = true;

    const loadCoverageYear = async () => {
      if (!selectedCoverageYear) {
        setCoverageYearRows([]);
        setIsCoverageYearLoading(false);
        return;
      }

      setIsCoverageYearLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
        const countryFilter = isGlobalScope ? '' : buildIso3SqlFilter(allowedIso3Values);

        const query = `
          SELECT
            ${WUENIC_COVERAGE_COLUMNS}
          FROM thrive_appdb_prd.wuenic_data_coverage w
          WHERE w.iso3 IS NOT NULL
            AND TRIM(w.iso3) <> ''
            AND w.vaccine IS NOT NULL
            AND TRIM(w.vaccine) <> ''
            AND w.year = ${Number(selectedCoverageYear)}
            ${countryFilter}
          ORDER BY w.country, w.vaccine;
        `;

        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({ query }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Coverage country request failed (${response.status})`);
        }

        const payload = await response.json();
        const resultRows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);
        if (!mounted) return;
        setCoverageYearRows(resultRows);
      } catch (error) {
        if (!mounted) return;
        setCoverageYearRows([]);
      } finally {
        if (mounted) setIsCoverageYearLoading(false);
      }
    };

    loadCoverageYear();

    return () => {
      mounted = false;
    };
  }, [allowedIso3Values, apiBase, normalizedAccessLevel, selectedCoverageYear]);

  const coverageCountryRows = useMemo(
    () => mergeCoverageRows(coverageYearRows, coverageTrendRows),
    [coverageTrendRows, coverageYearRows]
  );
  const isCoverageCountriesLoading = isCoverageYearLoading || isCoverageTrendLoading;
  const coverageAvailableYears = coverageYearOptions;

  useEffect(() => {
    if (coverageAvailableYears.length === 0) {
      if (selectedCoverageYear) setSelectedCoverageYear('');
      return;
    }

    if (!coverageAvailableYears.includes(selectedCoverageYear)) {
      setSelectedCoverageYear(coverageAvailableYears[0]);
    }
  }, [coverageAvailableYears, selectedCoverageYear]);

  const filteredCoverageCountryRows = useMemo(() => {
    if (!selectedCoverageYear) return coverageCountryRows;
    return coverageCountryRows.filter((row) => String(row?.year || '').trim() === selectedCoverageYear);
  }, [coverageCountryRows, selectedCoverageYear]);

  const coverageCountryProfiles = useMemo(() => {
    const countries = new Map();
    const gaviCountryByIso3 = new Map();
    homeGaviCountryRows.forEach((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      if (!iso3) return;
      gaviCountryByIso3.set(iso3, String(row?.Gavi_Country || row?.gavi_country || '').trim().toLowerCase() === 'yes');
    });

    filteredCoverageCountryRows.forEach((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      const vaccine = String(row?.vaccine || '').trim();
      if (!iso3 || !vaccine) return;

      if (!countries.has(iso3)) {
        countries.set(iso3, {
          key: iso3,
          iso3,
          countryName: String(row?.country || iso3).trim(),
          isGaviCountry: gaviCountryByIso3.get(iso3) === true,
          antigens: []
        });
      }

      countries.get(iso3).antigens.push({
        vaccine,
        year: row?.year,
        country: row?.country,
        wuenic: parseMetricNumber(row?.wuenic),
        wuenicPreviousRevision: parseMetricNumber(row?.wuenic_previous_revision),
        gradeOfConfidence: row?.grade_of_confidence,
        administrativeCoverage: parseMetricNumber(row?.administrative_coverage),
        governmentEstimate: parseMetricNumber(row?.government_estimate),
        childrenVaccinated: parseMetricNumber(row?.children_vaccinated),
        childrenInTarget: parseMetricNumber(row?.children_in_target),
        birthsUnpd: parseMetricNumber(row?.births_unpd),
        survivingInfantsUnpd: parseMetricNumber(row?.surviving_infants_unpd),
        comment: row?.comment
      });
    });

    return Array.from(countries.values()).sort((a, b) => a.countryName.localeCompare(b.countryName));
  }, [filteredCoverageCountryRows, homeGaviCountryRows]);

  const filteredCoverageCountryProfiles = useMemo(() => {
    if (coverageCountryTypeFilter === 'gavi') {
      return coverageCountryProfiles.filter((country) => country.isGaviCountry);
    }
    if (coverageCountryTypeFilter === 'non-gavi') {
      return coverageCountryProfiles.filter((country) => !country.isGaviCountry);
    }
    return coverageCountryProfiles;
  }, [coverageCountryProfiles, coverageCountryTypeFilter]);

  const coverageAntigenColumns = useMemo(() => {
    const vaccines = new Set();
    filteredCoverageCountryProfiles.forEach((country) => {
      country.antigens.forEach((record) => vaccines.add(record.vaccine));
    });
    return Array.from(vaccines).sort((a, b) => a.localeCompare(b));
  }, [filteredCoverageCountryProfiles]);

  const selectedCoverageCountry = useMemo(() => {
    return filteredCoverageCountryProfiles.find((country) => country.key === selectedCoverageCountryKey) || filteredCoverageCountryProfiles[0] || null;
  }, [filteredCoverageCountryProfiles, selectedCoverageCountryKey]);

  useEffect(() => {
    if (!selectedCoverageCountry) return;
    setSelectedCoverageCountryKey(selectedCoverageCountry.key);
  }, [selectedCoverageCountry]);

  const visibleCoverageCountries = useMemo(() => {
    return showAllCoverageCountries ? filteredCoverageCountryProfiles : filteredCoverageCountryProfiles.slice(0, 12);
  }, [filteredCoverageCountryProfiles, showAllCoverageCountries]);

  useEffect(() => {
    setShowAllCoverageCountries(false);
  }, [coverageCountryTypeFilter, selectedCoverageYear]);

  const homeCoverageSeriesByCountry = useMemo(() => {
    const countries = new Map();
    const rowsByCountryYear = new Map();

    coverageCountryRows.forEach((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      const countryName = String(row?.country || iso3).trim();
      const vaccine = String(row?.vaccine || '').trim().toUpperCase();
      const year = parseMetricNumber(row?.year);
      if (!iso3 || !year || !vaccine) return;

      if (!countries.has(iso3)) {
        countries.set(iso3, { key: iso3, iso3, countryName });
      }

      const mapKey = `${iso3}-${year}`;
      if (!rowsByCountryYear.has(mapKey)) {
        rowsByCountryYear.set(mapKey, {
          iso3,
          year,
          dtp3Values: [],
          zeroDoseValues: []
        });
      }

      const bucket = rowsByCountryYear.get(mapKey);
      if (vaccine === 'DTP3') {
        const wuenic = parseMetricNumber(row?.wuenic);
        if (wuenic !== null) bucket.dtp3Values.push(wuenic);
      }

      if (vaccine === 'DTP1') {
        const survivingInfants = parseMetricNumber(row?.surviving_infants_unpd);
        const childrenVaccinated = parseMetricNumber(row?.children_vaccinated);
        if (survivingInfants !== null && survivingInfants > 0 && childrenVaccinated !== null) {
          bucket.zeroDoseValues.push(Math.max(0, (1 - (childrenVaccinated / survivingInfants)) * 100));
        }
      }
    });

    const seriesByCountry = new Map();
    countries.forEach((country, iso3) => {
      seriesByCountry.set(iso3, {
        ...country,
        coverageSeries: [],
        zeroDoseSeries: []
      });
    });

    rowsByCountryYear.forEach((bucket) => {
      const country = seriesByCountry.get(bucket.iso3);
      if (!country) return;

      if (bucket.dtp3Values.length > 0) {
        country.coverageSeries.push({
          year: bucket.year,
          value: bucket.dtp3Values.reduce((sum, value) => sum + value, 0) / bucket.dtp3Values.length
        });
      }

      if (bucket.zeroDoseValues.length > 0) {
        country.zeroDoseSeries.push({
          year: bucket.year,
          value: bucket.zeroDoseValues.reduce((sum, value) => sum + value, 0) / bucket.zeroDoseValues.length
        });
      }
    });

    return Array.from(seriesByCountry.values())
      .map((country) => ({
        ...country,
        coverageSeries: country.coverageSeries.sort((a, b) => a.year - b.year),
        zeroDoseSeries: country.zeroDoseSeries.sort((a, b) => a.year - b.year)
      }))
      .sort((a, b) => a.countryName.localeCompare(b.countryName));
  }, [coverageCountryRows]);

  const homeBudgetSeriesByCountry = useMemo(() => {
    const rowsByCountryYear = new Map();

    homeBudgetRows.forEach((row) => {
      const iso3 = String(row?.country_iso3 || '').trim().toUpperCase();
      const year = parseMetricNumber(row?.year);
      if (!iso3 || !year) return;

      const mapKey = `${iso3}-${year}`;
      rowsByCountryYear.set(mapKey, (rowsByCountryYear.get(mapKey) || 0) + getHomeGaviTotalAmount(row));
    });

    const seriesByCountry = new Map();
    rowsByCountryYear.forEach((value, key) => {
      const [iso3, year] = key.split('-');
      if (!seriesByCountry.has(iso3)) seriesByCountry.set(iso3, []);
      seriesByCountry.get(iso3).push({ year: Number(year), value });
    });

    seriesByCountry.forEach((series) => {
      series.sort((a, b) => a.year - b.year);
    });

    return seriesByCountry;
  }, [homeBudgetRows]);

  const homeVciSeriesByCountry = useMemo(() => {
    const rowsByCountryYear = new Map();

    homeVciRows.forEach((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      const year = parseMetricNumber(row?.year);
      const score = getVciSummaryScore(row);
      if (!iso3 || !year || score === null) return;

      const mapKey = `${iso3}-${year}`;
      if (!rowsByCountryYear.has(mapKey)) rowsByCountryYear.set(mapKey, []);
      rowsByCountryYear.get(mapKey).push(score);
    });

    const seriesByCountry = new Map();
    rowsByCountryYear.forEach((values, key) => {
      const [iso3, year] = key.split('-');
      if (!seriesByCountry.has(iso3)) seriesByCountry.set(iso3, []);
      seriesByCountry.get(iso3).push({
        year: Number(year),
        value: values.reduce((sum, value) => sum + value, 0) / values.length
      });
    });

    seriesByCountry.forEach((series) => {
      series.sort((a, b) => a.year - b.year);
    });

    return seriesByCountry;
  }, [homeVciRows]);

  const homeGaviCountryMetadataByIso3 = useMemo(() => {
    const metadataByIso3 = new Map();
    homeGaviCountryRows.forEach((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      if (!iso3) return;
      metadataByIso3.set(iso3, {
        isGaviCountry: String(row?.Gavi_Country || row?.gavi_country || '').trim().toLowerCase() === 'yes',
        unicefRo: String(row?.UNICEF_RO || row?.unicef_ro || '').trim()
      });
    });
    return metadataByIso3;
  }, [homeGaviCountryRows]);

  const homeSummaryCountryRows = useMemo(() => {
    return homeCoverageSeriesByCountry.map((country) => ({
      ...country,
      isGaviCountry: homeGaviCountryMetadataByIso3.get(country.iso3)?.isGaviCountry === true,
      unicefRo: homeGaviCountryMetadataByIso3.get(country.iso3)?.unicefRo || 'N/A',
      budgetSeries: homeBudgetSeriesByCountry.get(country.iso3) || [],
      vciSeries: homeVciSeriesByCountry.get(country.iso3) || []
    }));
  }, [homeBudgetSeriesByCountry, homeCoverageSeriesByCountry, homeGaviCountryMetadataByIso3, homeVciSeriesByCountry]);

  const filteredHomeSummaryCountryRows = useMemo(() => {
    const iso3Filter = new Set(
      homeCountryFilterIso3Values.map((iso3) => String(iso3 || '').trim().toUpperCase()).filter(Boolean)
    );
    const countryRows = iso3Filter.size === 0
      ? homeSummaryCountryRows
      : homeSummaryCountryRows.filter((country) => iso3Filter.has(String(country.iso3 || '').trim().toUpperCase()));

    if (summaryCountryTypeFilter === 'gavi') {
      return countryRows.filter((country) => country.isGaviCountry);
    }
    if (summaryCountryTypeFilter === 'non-gavi') {
      return countryRows.filter((country) => !country.isGaviCountry);
    }
    return countryRows;
  }, [homeCountryFilterIso3Values, homeSummaryCountryRows, summaryCountryTypeFilter]);

  const visibleHomeSummaryCountryRows = useMemo(() => {
    return showAllSummaryCountries ? filteredHomeSummaryCountryRows : filteredHomeSummaryCountryRows.slice(0, 12);
  }, [filteredHomeSummaryCountryRows, showAllSummaryCountries]);

  useEffect(() => {
    setShowAllSummaryCountries(false);
  }, [filteredHomeSummaryCountryRows.length, summaryCountryTypeFilter]);

  const isSummaryMatrixLoading = isCoverageCountriesLoading || isHomeGaviFinancingLoading || isHomeVciLoading || isHomeGaviCountryLoading;

  const homeVaccineSafeSummary = useMemo(() => {
    const iso3Filter = new Set(homeCountryFilterIso3Values.map((iso3) => String(iso3 || '').trim().toUpperCase()).filter(Boolean));
    const rows = homeVciRows.filter((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      return iso3 && (iso3Filter.size === 0 || iso3Filter.has(iso3));
    });
    const years = rows
      .map((row) => parseMetricNumber(row?.year))
      .filter((year) => year !== null)
      .sort((a, b) => b - a);
    const latestYear = years[0] || null;

    if (!latestYear) return { value: null, year: null };

    const safeValues = rows
      .filter((row) => parseMetricNumber(row?.year) === latestYear)
      .map((row) => parseMetricNumber(row?.safe))
      .filter((value) => value !== null && value > 0);

    return {
      value: safeValues.length ? safeValues.reduce((sum, value) => sum + value, 0) / safeValues.length : null,
      year: latestYear
    };
  }, [homeCountryFilterIso3Values, homeVciRows]);
  const homeVaccineSafeDisplay = isHomeVciLoading ? 'Loading...' : formatSemanticPercent(homeVaccineSafeSummary.value);
  const homeGideonOutbreakCount = useMemo(() => {
    const iso3Filter = new Set(
      homeCountryFilterIso3Values.map((iso3) => String(iso3 || '').trim().toUpperCase()).filter(Boolean)
    );
    const countries = iso3Filter.size === 0
      ? homeOutbreakCountries
      : homeOutbreakCountries.filter((country) => iso3Filter.has(String(country?.iso3 || '').trim().toUpperCase()));
    return countActiveOutbreakCells(homeGideonAlertItems, countries);
  }, [homeCountryFilterIso3Values, homeGideonAlertItems, homeOutbreakCountries]);
  const homeNisPlannedDisplay = isHomeNisPlannedLoading
    ? 'Loading...'
    : homeNisPlannedCount === null
      ? 'N/A'
      : homeNisPlannedCount.toLocaleString();
  const homeZeroDoseSummary = useMemo(() => {
    const iso3Filter = new Set(homeCountryFilterIso3Values.map((iso3) => String(iso3 || '').trim().toUpperCase()).filter(Boolean));
    const dtp1Rows = coverageCountryRows.filter((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      const vaccine = String(row?.vaccine || '').trim().toUpperCase();
      return iso3 && vaccine === 'DTP1' && (iso3Filter.size === 0 || iso3Filter.has(iso3));
    });
    const years = dtp1Rows
      .map((row) => parseMetricNumber(row?.year))
      .filter((year) => year !== null)
      .sort((a, b) => b - a);
    const latestYear = years[0] || null;

    if (!latestYear) return { value: null, year: null };

    const totals = dtp1Rows
      .filter((row) => parseMetricNumber(row?.year) === latestYear)
      .reduce((acc, row) => {
        const survivingInfants = parseMetricNumber(row?.surviving_infants_unpd);
        const childrenVaccinated = parseMetricNumber(row?.children_vaccinated);
        return {
          survivingInfants: acc.survivingInfants + (survivingInfants || 0),
          childrenVaccinated: acc.childrenVaccinated + (childrenVaccinated || 0)
        };
      }, { survivingInfants: 0, childrenVaccinated: 0 });

    return {
      value: totals.survivingInfants > 0 ? Math.max(0, totals.survivingInfants - totals.childrenVaccinated) : null,
      year: latestYear
    };
  }, [coverageCountryRows, homeCountryFilterIso3Values]);
  const homeZeroDoseDisplay = isCoverageCountriesLoading ? 'Loading...' : formatCoverageNumber(homeZeroDoseSummary.value);

  const coverageKpis = useMemo(() => {
    const dtp3Records = filteredCoverageCountryProfiles
      .map((country) => findCoverageRecord(country, 'DTP3'))
      .filter(Boolean);
    const lowDtp3Countries = filteredCoverageCountryProfiles.filter((country) => {
      const value = findCoverageRecord(country, 'DTP3')?.wuenic;
      return value !== null && value < 80;
    }).length;
    const isCoverageViewLoading = isCoverageCountriesLoading || isHomeGaviCountryLoading;

    return [
      {
        key: 'countries',
        icon: Earth,
        value: isCoverageViewLoading ? '...' : filteredCoverageCountryProfiles.length.toLocaleString(),
        label: 'Countries in scope',
        meta: dynamicContextTitle
      },
      {
        key: 'dtp3',
        icon: TrendingUp,
        value: isCoverageViewLoading ? '...' : formatCoveragePercent(averageMetric(dtp3Records, 'wuenic')),
        label: 'Average DTP3 coverage',
        meta: selectedCoverageYear || 'Selected year'
      },
      {
        key: 'zero-dose',
        icon: Syringe,
        value: isCoverageViewLoading ? '...' : coverageAntigenColumns.length.toLocaleString(),
        label: 'Antigens in matrix',
        meta: 'All available vaccines'
      },
      {
        key: 'low-coverage',
        icon: CircleDot,
        value: isCoverageViewLoading ? '...' : lowDtp3Countries.toLocaleString(),
        label: 'DTP3 below 80%',
        meta: 'Countries needing attention'
      }
    ];
  }, [coverageAntigenColumns.length, dynamicContextTitle, filteredCoverageCountryProfiles, isCoverageCountriesLoading, isHomeGaviCountryLoading, selectedCoverageYear]);

  useEffect(() => {
    let mounted = true;

    const loadTaTickets = async () => {
      setIsTaLoading(true);
      try {
        const datasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
        const workspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
        const daxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
        const tableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';

        const baseQuery = String(daxQuery || '').trim();
        const baseTableName = String(tableName || '').trim();
        const safeTableName = baseTableName.replace(/'/g, "''");
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';

        let effectiveQuery = baseQuery || undefined;
        if (safeTableName && !isGlobalScope && allowedCountryValues.length > 0) {
          const countryLiterals = allowedCountryValues
            .map((country) => `"${country.replace(/"/g, '""')}"`)
            .join(', ');
          effectiveQuery = `EVALUATE FILTER('${safeTableName}', '${safeTableName}'[Country] IN {${countryLiterals}})`;
        }

        const effectiveTableName = effectiveQuery ? undefined : (baseTableName || undefined);
        const data = await fetchPowerBISemanticData(
          datasetId,
          workspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );
        if (!mounted) return;
        setTaRows(Array.isArray(data?.data) ? data.data : []);
        setTaColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (error) {
        if (!mounted) return;
        setTaRows([]);
        setTaColumns([]);
      } finally {
        if (mounted) setIsTaLoading(false);
      }
    };

    loadTaTickets();

    return () => {
      mounted = false;
    };
  }, [normalizedAccessLevel, allowedCountryValues]);

  const taLeadColumnKey = useMemo(
    () => findColumnKey(taColumns, ['ta lead staff (accountable)']),
    [taColumns]
  );
  const countryColumnKey = useMemo(
    () => findColumnKey(taColumns, ['country']),
    [taColumns]
  );
  const leadCoeTeamColumnKey = useMemo(
    () => findColumnKey(taColumns, ['lead coe team (accountable)', 'lead coe team', 'lead coe', 'coe team']),
    [taColumns]
  );
  const titleColumnKey = useMemo(
    () => findColumnKey(taColumns, [
      'request title',
      'request',
      'title',
      'activity',
      'intervention',
      'action',
      'description',
      'issue'
    ]),
    [taColumns]
  );
  const statusColumnKey = useMemo(
    () => findColumnKey(taColumns, ['implementation status', 'status']),
    [taColumns]
  );
  const assignedPersonColumnKey = useMemo(
    () => findColumnKey(taColumns, ['assigned person', 'assigned to', 'assignee', 'person assigned', 'responsible person']),
    [taColumns]
  );
  const initiatorColumnKey = useMemo(
    () => findColumnKey(taColumns, ['initiator', 'request initiator', 'created by', 'submitted by', 'requester', 'requestor']),
    [taColumns]
  );
  const normalizedUserIdentityValues = useMemo(
    () => [
      displayName,
      user?.email,
      user?.name,
      `${user?.name || ''} ${user?.lastName || ''}`.trim()
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter((value, idx, arr) => value && value !== 'user' && arr.indexOf(value) === idx),
    [displayName, user?.email, user?.lastName, user?.name]
  );

  const isCurrentUserInTaField = useCallback((value) => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if (!normalizedValue || normalizedUserIdentityValues.length === 0) return false;

    return normalizedUserIdentityValues.some((identity) => (
      normalizedValue === identity
      || normalizedValue.includes(`<${identity}>`)
      || normalizedValue.split(/[;,|/]/).some((part) => part.trim() === identity)
    ));
  }, [normalizedUserIdentityValues]);

  const displayedTaTickets = useMemo(() => {
    if (!Array.isArray(taRows) || taRows.length === 0) return [];

    const userColumnKeys = [taLeadColumnKey, assignedPersonColumnKey, initiatorColumnKey]
      .filter((columnKey, idx, arr) => columnKey && arr.indexOf(columnKey) === idx);
    if (userColumnKeys.length === 0) return [];

    return taRows
      .filter((row) => userColumnKeys.some((columnKey) => isCurrentUserInTaField(row?.[columnKey])))
      .slice(0, 6);
  }, [
    taRows,
    taLeadColumnKey,
    assignedPersonColumnKey,
    initiatorColumnKey,
    isCurrentUserInTaField
  ]);

  const getTicketTitle = (row, idx) => {
    const preferred = titleColumnKey ? String(row?.[titleColumnKey] || '').trim() : '';
    if (preferred) return preferred;
    if (taLeadColumnKey) {
      const owner = String(row?.[taLeadColumnKey] || '').trim();
      if (owner) return `TA request for ${owner}`;
    }
    return `TA ticket ${idx + 1}`;
  };

  const getTicketMeta = (row) => {
    const statusText = statusColumnKey ? String(row?.[statusColumnKey] || '').trim() : '';
    return statusText;
  };

  const getTicketPills = (row) => {
    const countryText = countryColumnKey ? String(row?.[countryColumnKey] || '').trim() : '';
    const leadCoeTeamText = leadCoeTeamColumnKey ? String(row?.[leadCoeTeamColumnKey] || '').trim() : '';
    return [
      countryText ? { label: countryText, className: 'is-country' } : null,
      leadCoeTeamText ? { label: leadCoeTeamText, className: 'is-lead-coe' } : null
    ].filter(Boolean);
  };

  const createArticlePreview = (text) => {
    const normalizedText = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalizedText) return 'No summary available.';
    if (normalizedText.length <= 180) return normalizedText;
    return `${normalizedText.slice(0, 177).trimEnd()}...`;
  };

  const truncateArticleTitle = (title) => {
    const normalizedTitle = String(title || '').replace(/\s+/g, ' ').trim();
    if (!normalizedTitle) return 'WHO Disease Outbreak News';
    if (normalizedTitle.length <= 100) return normalizedTitle;
    return `${normalizedTitle.slice(0, 97).trimEnd()}...`;
  };

  useEffect(() => {
    let mounted = true;

    const loadHomeNisPlannedCount = async () => {
      setIsHomeNisPlannedLoading(true);
      try {
        const data = await fetchPowerBIFabricSqlData({
          tableName: GAVI_FINANCE_UPDATE_TABLE_NAME,
          maxRows: 5000
        });

        const rows = Array.isArray(data?.data) ? data.data : [];
        const columns = Array.isArray(data?.columns) ? data.columns : [];
        const countryColumnKey = findColumnKeyByPrefix(columns, 'country');
        const submissionColumnKey = findColumnKeyByPrefix(columns, 'submission to gavi');
        const reprogrammingSubmissionColumnKey = findColumnKeyByPrefix(columns, 'reprogramming submission');
        const countryFilter = new Set(homeCountryFilterNames.map((country) => normalizeFinanceCountryName(country)).filter(Boolean));
        const shouldFilterCountries = countryFilter.size > 0;
        const plannedCount = rows.filter((row) => {
          if (shouldFilterCountries) {
            const country = normalizeFinanceCountryName(countryColumnKey ? row?.[countryColumnKey] : '');
            if (!countryFilter.has(country)) return false;
          }

          const submissionYear = submissionColumnKey ? getYearFromText(row?.[submissionColumnKey]) : null;
          if (submissionYear === HOME_NIS_PLANNING_YEAR) return true;
          if (submissionYear) return false;
          const reprogrammingYear = reprogrammingSubmissionColumnKey
            ? getYearFromText(row?.[reprogrammingSubmissionColumnKey])
            : null;
          return reprogrammingYear === HOME_NIS_PLANNING_YEAR;
        }).length;

        if (!mounted) return;
        setHomeNisPlannedCount(plannedCount);
      } catch (error) {
        if (!mounted) return;
        setHomeNisPlannedCount(null);
      } finally {
        if (mounted) setIsHomeNisPlannedLoading(false);
      }
    };

    loadHomeNisPlannedCount();

    return () => {
      mounted = false;
    };
  }, [homeCountryFilterNames]);

  useEffect(() => {
    let mounted = true;

    const loadHomeGideonOutbreaks = async () => {
      setIsHomeGideonOutbreaksLoading(true);
      setHomeGideonOutbreakError(false);
      try {
        const idToken = await authService.getIdToken();
        const [alertsResponse, countriesResponse] = await Promise.all([
          fetch(`${apiBase}/gideon-outbreaks?count=${GIDEON_NEWS_LIMIT}&days=${GIDEON_NEWS_DAYS}&since=${GIDEON_ALERT_SINCE_DAYS}`, {
            method: 'GET',
            mode: 'cors'
          }),
          fetch(`${apiBase}/db-query`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`
            },
            body: JSON.stringify({
              query: `
                SELECT DISTINCT
                  si.iso3,
                  ga.Country_Name,
                  ga.Gavi_Country
                FROM thrive_appdb_prd.summarized_indicators si
                LEFT JOIN thrive_appdb_prd.geo_admin ga
                  ON ga.iso3 = si.iso3
                WHERE LOWER(TRIM(COALESCE(ga.thrive360_status, ''))) = 'reporting'
                  AND LOWER(TRIM(COALESCE(ga.Gavi_Country, ''))) = 'yes'
                  AND (
                    si.indicator IN (
                      'DTP3 (2024) - WUENIC',
                      'Pol3 (2024) - WUENIC',
                      'MCV1 (2024) - WUENIC',
                      'DTP1 (2024) - Surviving Infants UNPD',
                      'DTP1 (2024) - Children Vaccinated',
                      'BCG (2024) - Children in Target'
                    )
                    OR ((LOWER(si.indicator) LIKE 'cholera%' OR LOWER(si.indicator) LIKE 'ocv%') AND LOWER(si.indicator) LIKE '%wuenic%')
                    OR ((LOWER(si.indicator) LIKE 'yellow fever%' OR LOWER(si.indicator) LIKE 'yfv%') AND LOWER(si.indicator) LIKE '%wuenic%')
                    OR ((LOWER(si.indicator) LIKE 'pol3%' OR LOWER(si.indicator) LIKE 'polio%') AND LOWER(si.indicator) LIKE '%wuenic%')
                    OR ((LOWER(si.indicator) LIKE 'mcv1%' OR LOWER(si.indicator) LIKE 'measles%') AND LOWER(si.indicator) LIKE '%wuenic%')
                    OR (LOWER(si.indicator) LIKE 'bcg%' AND LOWER(si.indicator) LIKE '%children in target%')
                    OR LOWER(si.indicator) IN ('total number of stores', 'number of stores')
                    OR LOWER(si.indicator) LIKE '%total%stores%'
                    OR LOWER(si.indicator) LIKE '%number%of%stores%'
                  )
                ORDER BY ga.Country_Name;
              `
            }),
            mode: 'cors'
          })
        ]);

        if (!alertsResponse.ok) {
          throw new Error(`GIDEON outbreak request failed (${alertsResponse.status})`);
        }
        if (!countriesResponse.ok) {
          throw new Error(`Outbreak country request failed (${countriesResponse.status})`);
        }

        const payload = await alertsResponse.json();
        const countriesPayload = await countriesResponse.json();
        const alertItems = Array.isArray(payload?.alertItems) ? payload.alertItems : [];
        const countryRows = Array.isArray(countriesPayload?.result)
          ? countriesPayload.result
          : (Array.isArray(countriesPayload) ? countriesPayload : []);

        if (!mounted) return;
        setHomeGideonAlertItems(alertItems);
        setHomeOutbreakCountries(countryRows.map((row) => toOutbreakCountry(row)));
      } catch (error) {
        if (!mounted) return;
        setHomeGideonAlertItems([]);
        setHomeOutbreakCountries([]);
        setHomeGideonOutbreakError(true);
      } finally {
        if (mounted) setIsHomeGideonOutbreaksLoading(false);
      }
    };

    loadHomeGideonOutbreaks();

    return () => {
      mounted = false;
    };
  }, [apiBase]);

  useEffect(() => {
    let mounted = true;

    const loadWhoArticles = async () => {
      setIsWhoArticlesLoading(true);
      try {
        const response = await fetch(whoNewsApiUrl, { method: 'GET', mode: 'cors' });
        if (!response.ok) {
          throw new Error(`WHO scrape request failed (${response.status})`);
        }

        const payload = await response.json();
        const items = Array.isArray(payload) ? payload : [];
        const formattedItems = items
          .map((item) => ({
            title: String(item?.title || '').trim(),
            text: String(item?.text || '').trim(),
            url: String(item?.url || '').trim(),
            date: String(item?.date || '').trim()
          }))
          .filter((item) => item.title || item.text || item.url || item.date)
          .slice(0, 3);

        if (!mounted) return;
        setWhoArticles(formattedItems);
      } catch (error) {
        if (!mounted) return;
        setWhoArticles([]);
      } finally {
        if (mounted) setIsWhoArticlesLoading(false);
      }
    };

    loadWhoArticles();

    return () => {
      mounted = false;
    };
  }, [whoNewsApiUrl]);

  const thriveSubpages = [
    { key: 'stock-performance', label: 'THRIVE360' },
    { key: 'country-instances', label: 'Country Instances' },
    { key: 'drive', label: 'DRIVE' },
    { key: 'evma', label: 'EVMA' },
    { key: 'cold-chain-inventory-gaps', label: 'Inventory & Gaps' },
    { key: 'ticketing-system', label: 'Ticketing System' },
    { key: 'data-warehouse', label: 'Data Warehouse' }
  ];

  const placeholderPages = {
    'global-coverage': 'Global Coverage',
    'new-vaccines': 'Vaccines Programmes',
    'demand-vaccine-trust': 'Demand & Vaccine Trust',
    outbreaks: 'Outbreaks',
    budgets: 'Financing',
    'scip-tool': 'SCIP Tool',
    'scip-tool-detail': 'CIP Details',
    'management-kpi': 'Management KPI',
    'knowledge-country-guideline': 'Country Guideline on Immunization',
    'knowledge-global-tool': 'Global Tool for Immunization',
    'global-stock-monitoring': 'Global Stock Monitoring',
    'thrive-country-view': 'Thrive360 Country View',
    'coverage-wuenic': 'Vaccination coverage rates (e.g., DTP1, DTP3)',
    'coverage-cluster': 'Zero-dose and under-immunized populations',
    'coverage-demographics': 'Geographic disparities (district, region, facility level)',
    'coverage-events': 'Trends and cohort analysis',
    'coverage-admin': 'Dropout rates',
    'coverage-surveys': 'Integration with WUENIC and survey data',
    'vaccines-new-introductions': 'Planning and tracking new vaccine introductions (e.g., HPV, malaria, Rota)',
    'vaccines-coverage': 'Coverage and uptake monitoring post-introduction',
    'vaccines-safety': 'Safety and adverse event tracking (AEFI linkage if applicable)',
    'vaccines-readiness': 'Operational readiness (training, supply chain readiness)',
    'budgets-allocation': 'Budget allocation by programme area (routine immunization, campaigns, cold chain, vaccine procurement, logistics, SBC)',
    'budgets-tracking': 'Expenditure tracking vs. planned budgets',
    'budgets-financing': 'Donor funding vs. domestic financing',
    'budgets-efficiency': 'Cost-efficiency and cost-per-dose metrics',
    'budgets-forecasting': 'Forecasting and financial gap analysis',
    'management-kpi-core': 'Core KPIs (coverage, dropout rates, stock availability)',
    'management-kpi-cold-chain': 'Cold chain performance indicators',
    'management-kpi-service-delivery': 'Service delivery reliability (session completion, outreach)',
    'management-kpi-data-quality': 'Data quality indicators',
    'management-kpi-early-warning': 'Early warning signals for system failure'
  };

  const sidebarTooltips = {
    thrive360: 'Vaccine stock performance monitoring, risk management, collaboration, ticketing, tracking, and data warehouse tools.',
    'global-coverage': 'Explore immunization coverage rates, zero-dose populations, trends, dropout rates, and survey data.',
    'new-vaccines': 'Plan and monitor new vaccine introductions, rollout readiness, uptake, and safety signals.',
    'demand-vaccine-trust': 'Monitor vaccine demand, confidence, acceptance, and trust-building activities.',
    outbreaks: 'Track outbreak-related immunization needs and response activity.',
    budgets: 'Plan, track, and optimize immunization programme budgets, financing, and spending.',
    'scip-tool': 'Review Supply Chain Improvement Planning CIPs for your access scope.',
    'management-kpi': 'Review management KPIs for coverage, stock availability, cold chain, service delivery, data quality, and early warnings.'
  };

  const coverageDashboardViews = {
    'global-coverage': {
      title: 'Global Coverage Data',
      description: 'Immunization coverage dashboard from THRIVE360 coverage data.',
      pageName: 'global-coverage'
    },
    'coverage-wuenic': {
      title: 'Vaccination Coverage Rates',
      description: 'Focused view for DTP1, DTP3 and other immunization coverage rates.',
      pageName: 'coverage-wuenic'
    },
    'coverage-cluster': {
      title: 'Zero-Dose and Under-Immunized Populations',
      description: 'Focused view for zero-dose and under-immunized population analysis.',
      pageName: 'coverage-cluster'
    },
    'coverage-demographics': {
      title: 'Geographic Disparities',
      description: 'Focused view for district, region and facility-level disparities.',
      pageName: 'coverage-demographics'
    },
    'coverage-events': {
      title: 'Trends and Cohort Analysis',
      description: 'Focused view for coverage trends and cohort analysis.',
      pageName: 'coverage-events'
    },
    'coverage-admin': {
      title: 'Dropout Rates',
      description: 'Focused view for dropout rate monitoring.',
      pageName: 'coverage-admin'
    },
    'coverage-surveys': {
      title: 'WUENIC and Survey Data',
      description: 'Focused view for WUENIC and survey data integration.',
      pageName: 'coverage-surveys'
    }
  };

  const selectedPlaceholderPage = activePlaceholderPage ? placeholderPages[activePlaceholderPage] || null : null;
  const embeddedUserClaims = useMemo(
    () => ({
      role: user?.role || user?.accessLevel || 'User',
      email: user?.email || '',
      countryCode: user?.country_iso3 || ''
    }),
    [user?.role, user?.accessLevel, user?.email, user?.country_iso3]
  );
  const canAccessSection = (section) => {
    if (normalizedAccessLevel === 'admin') return true;
    const allowedSections = Array.isArray(user?.allowedSections) ? user.allowedSections : [];
    return allowedSections.includes(section);
  };
  const canAccessBudgets = canAccessSection('budgets');
  const canAccessScip = canAccessSection('scip');
  const canAccessManagementKpi = normalizedAccessLevel === 'admin' || normalizedAccessLevel === 'global';

  useEffect(() => {
    if (activePlaceholderPage === 'management-kpi' && !canAccessManagementKpi) {
      setActivePlaceholderPage(null);
    }
  }, [activePlaceholderPage, canAccessManagementKpi]);
  const homeMainKpiCards = [
    {
      key: 'global-coverage',
      label: 'Global Coverage',
      value: dtp3CoverageDisplay,
      meta: `DTP3 coverage | target ${targetPopulationDisplay}`,
      icon: Earth,
      iconClassName: 'kpi-icon-blue',
      placeholderPage: 'global-coverage'
    },
    {
      key: 'zero-dose',
      label: 'Zero Dose',
      value: homeZeroDoseDisplay,
      meta: `DTP1 zero-dose children${homeZeroDoseSummary.year ? ` (${homeZeroDoseSummary.year})` : ''}`,
      icon: CircleDot,
      iconClassName: 'kpi-icon-red',
      placeholderPage: 'global-coverage'
    },
    {
      key: 'new-vaccines',
      label: 'Vaccines Programmes',
      value: homeNewVaccineIntroDisplay,
      meta: 'New vaccine introductions planned',
      icon: Syringe,
      iconClassName: 'kpi-icon-green',
      placeholderPage: 'new-vaccines'
    },
    {
      key: 'vaccine-cold-chain',
      label: 'Vaccine & Cold Chain',
      value: homeWorkingEquipmentDisplay,
      meta: 'Cold Chain Equipment functional status',
      metrics: [
        {
          value: homeWorkingEquipmentDisplay,
          meta: 'CCE functional'
        },
        {
          value: homeStockAvailabilityDisplay,
          meta: 'Stock full availability'
        }
      ],
      icon: LayoutDashboard,
      iconClassName: 'kpi-icon-purple',
      thriveSubpage: 'vccm'
    },
    {
      key: 'demand-vaccine-trust',
      label: 'Demand & Trust',
      value: homeVaccineSafeDisplay,
      meta: 'Vaccines are Safe (2024) VCI',
      icon: HeartHandshake,
      iconClassName: 'kpi-icon-orange',
      placeholderPage: 'demand-vaccine-trust'
    },
    {
      key: 'outbreaks',
      label: 'Outbreaks',
      value: isHomeGideonOutbreaksLoading
        ? 'Loading...'
        : homeGideonOutbreakError
          ? 'N/A'
          : homeGideonOutbreakCount.toLocaleString(),
      meta: 'Active outbreaks',
      icon: ShieldCheck,
      iconClassName: 'kpi-icon-red',
      placeholderPage: 'outbreaks'
    },
    canAccessBudgets && {
      key: 'budgets',
      label: 'Budgets',
      value: homeGaviFinancingDisplay,
      meta: `GAVI financing ${HOME_GAVI_FINANCING_YEAR}`,
      icon: Wallet,
      iconClassName: 'kpi-icon-blue',
      placeholderPage: 'budgets'
    },
    canAccessScip && {
      key: 'planning-monitoring',
      label: 'Planning & Monitoring',
      value: homeNisPlannedDisplay,
      meta: `NIS planned for ${HOME_NIS_PLANNING_YEAR}`,
      icon: ClipboardList,
      iconClassName: 'kpi-icon-green',
      placeholderPage: 'scip-tool'
    }
  ].filter(Boolean);

  const renderColdChainInventoryGapsContent = () => <ColdChainInventoryGaps />;

  const renderDriveDashboardContent = () => (
    <section className="home-section global-coverage-section">
      <div className="global-coverage-card">
        <div className="global-coverage-header">
          <h2 className="global-coverage-title">DRIVE - Last Mile Delivery Dashboard</h2>
          <p className="global-coverage-description">
            Last mile delivery monitoring dashboard from THRIVE360.
          </p>
        </div>
        <div className="global-coverage-embed">
          <PowerBIEmbed
            reportId={DRIVE_REPORT_ID}
            workspaceId={DRIVE_WORKSPACE_ID}
            height="calc(100vh - 210px)"
          />
        </div>
      </div>
    </section>
  );

  const renderEvmaDashboardContent = () => (
    <section className="home-section global-coverage-section">
      <div className="global-coverage-card">
        <div className="global-coverage-header">
          <h2 className="global-coverage-title">EVMA Dashboard</h2>
          <p className="global-coverage-description">
            EVMA monitoring dashboard from THRIVE360.
          </p>
        </div>
        <div className="global-coverage-embed">
          <PowerBIEmbed
            reportId={EVMA_REPORT_ID}
            workspaceId={EVMA_WORKSPACE_ID}
            pageName="Main"
            showPageNavigator
            height="calc(100vh - 210px)"
          />
        </div>
      </div>
    </section>
  );

  const renderThriveSubpageContent = () => {
    const sharedProps = { userClaims: embeddedUserClaims, isEmbedded: true };

    switch (activeThriveSubpage) {
      case 'vccm':
        return (
          <VCCM
            countryRows={homeSummaryCountryRows}
            evmaMetricRows={vccmEvmaMetricRows}
            isEvmaMetricsLoading={isVccmEvmaMetricsLoading}
            isCountryMatrixLoading={isSummaryMatrixLoading}
            isStockMetricsLoading={isVccmStockMetricsLoading}
            isTssLoading={isVccmTssLoading}
            onSelectSubpage={setActiveThriveSubpage}
            stockMetricRows={vccmStockMetricRows}
            tssRows={vccmTssRows}
          />
        );
      case 'stock-performance':
        return <StockPerformance {...sharedProps} />;
      case 'drive':
        return renderDriveDashboardContent();
      case 'evma':
        return renderEvmaDashboardContent();
      case 'cold-chain-inventory-gaps':
        return renderColdChainInventoryGapsContent();
      case 'country-instances':
        return <COInstance {...sharedProps} />;
      case 'ticketing-system':
        return <TicketSystem {...sharedProps} />;
      case 'tracking':
        return <TrackingSystem {...sharedProps} />;
      case 'co-feedback':
        return <COFeedback {...sharedProps} />;
      case 'data-warehouse':
        return <DataWarehouse {...sharedProps} />;
      default:
        return null;
    }
  };

  const renderGlobalCoverageContent = () => {
    const isCoverageViewLoading = isCoverageCountriesLoading || isHomeGaviCountryLoading;
    const selectedDtp1Record = findCoverageRecord(selectedCoverageCountry, 'DTP1');
    const selectedDtp3Record = findCoverageRecord(selectedCoverageCountry, 'DTP3');
    const zeroDoseValue = selectedDtp1Record?.survivingInfantsUnpd > 0 && selectedDtp1Record?.childrenVaccinated != null
      ? (1 - (selectedDtp1Record.childrenVaccinated / selectedDtp1Record.survivingInfantsUnpd)) * 100
      : null;
    const dropoutValue = selectedDtp1Record?.wuenic != null && selectedDtp3Record?.wuenic != null
      ? Math.max(selectedDtp1Record.wuenic - selectedDtp3Record.wuenic, 0)
      : null;
    const selectedAntigenRecords = selectedCoverageCountry
      ? [...selectedCoverageCountry.antigens].sort((a, b) => a.vaccine.localeCompare(b.vaccine))
      : [];

    return (
      <section className="home-section vaccines-page-section vaccines-dashboard global-coverage-dashboard">
        <div className="vaccines-dashboard-header">
          <div>
            <p className="vaccines-section-label">WUENIC Coverage - {selectedCoverageYear || 'loading years'}</p>
            <h2 className="vaccines-dashboard-title">Global coverage monitoring</h2>
            <p className="vaccines-dashboard-subtitle">
              Review country immunization coverage by antigen with WUENIC, administrative coverage and government estimate signals.
            </p>
          </div>
          <label className="coverage-year-select">
            <span>Year</span>
            <select
              value={selectedCoverageYear}
              onChange={(event) => {
                setSelectedCoverageYear(event.target.value);
                setShowAllCoverageCountries(false);
              }}
              disabled={isCoverageCountriesLoading || coverageAvailableYears.length === 0}
            >
              {coverageAvailableYears.length === 0 && <option value="">Loading...</option>}
              {coverageAvailableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="vaccines-kpi-row">
          {coverageKpis.map((item) => {
            const Icon = item.icon;

            return (
              <article key={item.key} className={`vaccines-kpi-card vaccines-kpi-${item.key}`}>
                <span className="vaccines-kpi-icon" aria-hidden="true">
                  <Icon size={18} />
                </span>
                <div>
                  <p className="vaccines-kpi-value">{item.value}</p>
                  <p className="vaccines-kpi-label">{item.label}</p>
                  <p className="vaccines-kpi-meta">{item.meta}</p>
                </div>
              </article>
            );
          })}
        </div>

        <div className="vaccines-main-grid">
          <div className="vaccines-left-column">
            <div className="vaccines-schedule-card vaccines-matrix-card">
              <div className="vaccines-card-header">
                <div>
                  <p className="vaccines-section-label">Coverage x Country Matrix</p>
                  <h3 className="vaccines-card-title">Coverage performance by antigen</h3>
                </div>
                <div className="vaccines-matrix-actions">
                  <div className="vaccines-segmented-control" aria-label="Country funding filter">
                    <button
                      type="button"
                      className={coverageCountryTypeFilter === 'gavi' ? 'is-active' : ''}
                      onClick={() => setCoverageCountryTypeFilter('gavi')}
                    >
                      Gavi Countries
                    </button>
                    <button
                      type="button"
                      className={coverageCountryTypeFilter === 'non-gavi' ? 'is-active' : ''}
                      onClick={() => setCoverageCountryTypeFilter('non-gavi')}
                    >
                      Non-Gavi Countries
                    </button>
                    <button
                      type="button"
                      className={coverageCountryTypeFilter === 'all' ? 'is-active' : ''}
                      onClick={() => setCoverageCountryTypeFilter('all')}
                    >
                      All
                    </button>
                  </div>
                  <div className="vaccines-matrix-legend" aria-label="Coverage matrix color legend">
                    <span><i className="matrix-status is-coverage-high" />90%+</span>
                    <span><i className="matrix-status is-coverage-medium" />80-89%</span>
                    <span><i className="matrix-status is-coverage-low" />Below 80%</span>
                    <span><i className="coverage-marker-example is-wuenic" />WUENIC</span>
                    <span><i className="coverage-marker-example is-admin" />Admin/Gov</span>
                  </div>
                </div>
              </div>
              <div className="vaccines-matrix-wrap">
                <table className="vaccines-matrix-table coverage-matrix-table" aria-label="Coverage country matrix">
                  <thead>
                    <tr>
                      <th scope="col">Country</th>
                      {coverageAntigenColumns.map((vaccine) => (
                        <th key={vaccine} scope="col">{vaccine}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isCoverageViewLoading && (
                      <tr>
                        <td colSpan={coverageAntigenColumns.length + 1}>Loading coverage data...</td>
                      </tr>
                    )}
                    {!isCoverageViewLoading && visibleCoverageCountries.length === 0 && (
                      <tr>
                        <td colSpan={coverageAntigenColumns.length + 1}>No coverage countries found for this view.</td>
                      </tr>
                    )}
                    {!isCoverageViewLoading && visibleCoverageCountries.map((country) => (
                      <tr
                        key={country.key}
                        className={country.key === selectedCoverageCountryKey ? 'is-selected' : ''}
                        aria-selected={country.key === selectedCoverageCountryKey}
                      >
                        <th scope="row">
                          <button type="button" onClick={() => setSelectedCoverageCountryKey(country.key)}>
                            {country.countryName}
                          </button>
                        </th>
                        {coverageAntigenColumns.map((vaccine) => {
                          const record = findCoverageRecord(country, vaccine);
                          return (
                            <td key={`${country.key}-${vaccine}`}>
                              <span
                                className={`coverage-stripe ${getCoverageStatusClass(record?.wuenic)}`}
                                title={getCoverageRecordTooltip(country, record)}
                              >
                                {record && record.administrativeCoverage !== null && (
                                  <i className="coverage-marker is-admin" style={getCoverageMarkerStyle(record.administrativeCoverage)} />
                                )}
                                {record && record.governmentEstimate !== null && (
                                  <i className="coverage-marker is-government" style={getCoverageMarkerStyle(record.governmentEstimate)} />
                                )}
                                {record && record.wuenic !== null && (
                                  <i className="coverage-marker is-wuenic" style={getCoverageMarkerStyle(record.wuenic)} />
                                )}
                                {record && record.wuenic !== null && (
                                  <span className="coverage-stripe-value">{Math.round(record.wuenic)}%</span>
                                )}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredCoverageCountryProfiles.length > 12 && (
                <div className="vaccines-matrix-footer">
                  <p className="vaccines-footnote">
                    Showing {visibleCoverageCountries.length} of {filteredCoverageCountryProfiles.length} countries in this view.
                  </p>
                  <button
                    type="button"
                    className="vaccines-expand-button"
                    onClick={() => setShowAllCoverageCountries((current) => !current)}
                  >
                    {showAllCoverageCountries ? 'Show fewer countries' : `Show all ${filteredCoverageCountryProfiles.length} countries`}
                  </button>
                </div>
              )}
            </div>
          </div>

          <aside className="vaccines-detail-panel" aria-label="Country coverage details">
            <div className="vaccines-country-header">
              <div>
                <p className="vaccines-section-label">Country snapshot</p>
                <h3>{selectedCoverageCountry?.countryName || 'No country selected'}</h3>
              </div>
              {selectedCoverageCountry && (
                <span className={`country-badge ${getCoverageStatusClass(selectedDtp3Record?.wuenic)}`}>
                  {getCoverageStatusLabel(selectedDtp3Record?.wuenic)}
                </span>
              )}
            </div>

            <div className="vaccines-snapshot-grid">
              <div><strong>{formatCoveragePercent(selectedDtp3Record?.wuenic)}</strong><span>DTP3 WUENIC</span></div>
              <div><strong>{formatCoveragePercent(zeroDoseValue)}</strong><span>Zero-dose estimate</span></div>
              <div><strong>{formatCoveragePercent(dropoutValue)}</strong><span>DTP1-DTP3 dropout</span></div>
              <div><strong>{formatCoverageNumber(selectedDtp3Record?.childrenInTarget)}</strong><span>DTP3 target</span></div>
            </div>

            <div className="vaccines-panel-section">
              <h4>Coverage indicators</h4>
              <div className="vaccines-wuenic-table-wrap">
                <table className="vaccines-wuenic-table" aria-label="Country coverage indicators">
                  <thead>
                    <tr>
                      <th scope="col">Antigen</th>
                      <th scope="col">Year</th>
                      <th scope="col">WUENIC</th>
                      <th scope="col">Admin</th>
                      <th scope="col">Gov.</th>
                      <th scope="col">GoC</th>
                      <th scope="col">Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedCoverageCountry && (
                      <tr>
                        <td colSpan="7">Select a country to view coverage details.</td>
                      </tr>
                    )}
                    {selectedCoverageCountry && selectedAntigenRecords.map((record) => (
                      <tr key={`${selectedCoverageCountry.key}-${record.vaccine}`}>
                        <td>{record.vaccine}</td>
                        <td>{record.year || 'N/A'}</td>
                        <td>{formatCoveragePercent(record.wuenic)}</td>
                        <td>{formatCoveragePercent(record.administrativeCoverage)}</td>
                        <td>{formatCoveragePercent(record.governmentEstimate)}</td>
                        <td>{record.gradeOfConfidence || 'N/A'}</td>
                        <td>{formatCoverageNumber(record.childrenInTarget)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="vaccines-table-footnote">
                Stripe color is based on WUENIC coverage. The bold marker is WUENIC; hairline markers show administrative coverage and government estimate.
              </p>
            </div>
          </aside>
        </div>
      </section>
    );
  };

  const renderPlaceholderPageContent = () => {
    if (activePlaceholderPage === 'global-coverage') {
      return renderGlobalCoverageContent();
    }

    const coverageDashboardView = coverageDashboardViews[activePlaceholderPage];

    if (coverageDashboardView) {
      return (
        <section className="home-section global-coverage-section">
          <div className="global-coverage-card">
            <div className="global-coverage-header">
              <h2 className="global-coverage-title">{coverageDashboardView.title}</h2>
              <p className="global-coverage-description">
                {coverageDashboardView.description}
              </p>
            </div>
            <div className="global-coverage-embed">
              <PowerBIEmbed
                reportId="acb282f5-9e3d-401c-96d3-22ab31be3552"
                workspaceId="7d441575-4aa9-4260-99dd-543f002c0340"
                height="calc(100vh - 210px)"
                pageName={coverageDashboardView.pageName}
              />
            </div>
          </div>
        </section>
      );
    }

    if (activePlaceholderPage === 'new-vaccines') {
      return <NewVaccineIntro isEmbedded />;
    }

    if (activePlaceholderPage === 'demand-vaccine-trust') {
      return <DemandVaccineTrust isEmbedded />;
    }

    if (activePlaceholderPage === 'budgets') {
      return <Budgets isEmbedded />;
    }

    if (activePlaceholderPage === 'outbreaks') {
      return <Outbreaks userClaims={embeddedUserClaims} isEmbedded />;
    }

    if (activePlaceholderPage === 'scip-tool') {
      return <ImprovementPlanningJourney />;
    }

    if (activePlaceholderPage === 'scip-tool-detail') {
      return <SCIPToolDetail userClaims={embeddedUserClaims} isEmbedded backHref="/v2/scip-tool" />;
    }

    if (activePlaceholderPage === 'management-kpi') {
      return canAccessManagementKpi ? <ManagementKPI /> : null;
    }

    return (
      <section className="home-section placeholder-page-section">
        <div className="placeholder-page-card">
          <h2 className="placeholder-page-title">{selectedPlaceholderPage}</h2>
          <p className="placeholder-page-description">Blank placeholder page.</p>
        </div>
      </section>
    );
  };

  const suppressSidebarTooltip = (event) => {
    const button = event.target.closest('.simple-menu-link[data-tooltip]');
    if (!button) return;
    button.blur();
    button.classList.add('is-tooltip-suppressed');
  };

  const releaseSidebarTooltip = (event) => {
    const button = event.target.closest('.simple-menu-link');
    if (!button || button.contains(event.relatedTarget)) return;
    button.classList.remove('is-tooltip-suppressed');
  };

  return (
    <div className="v2-module">
      <div className={`app-layout ${isCollapsed ? 'is-sidebar-collapsed' : ''} ${isRightSidebarCollapsed ? 'is-right-sidebar-collapsed' : ''}`}>
        <div className="app-topbar">
          <div className="app-topbar-main">
            <div className="topbar-left">
              <div className="topbar-texts">
                <h1 className="page-title">IMMUNIZATION360 (BETA)</h1>
                <p className="page-subtitle">
                  Performance monitoring, integration analytics and colaboration
                </p>
              </div>
            </div>

            <button
              type="button"
              className="menu-toggle topbar-menu-right"
              onClick={() => setIsCollapsed((current) => !current)}
              aria-label="Toggle sidebar"
            >
              <Menu size={18} />
            </button>

            <button
              type="button"
              className="menu-toggle topbar-right-sidebar-toggle"
              onClick={() => setIsRightSidebarCollapsed((current) => !current)}
              aria-label={isRightSidebarCollapsed ? 'Expand right sidebar' : 'Collapse right sidebar'}
              aria-expanded={!isRightSidebarCollapsed}
            >
              {isRightSidebarCollapsed ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>

            <div className="app-topbar-user" aria-label="User info">
              <div className="user-avatar">{avatarInitials}</div>
              <div className="user-meta">
                <div className="user-name">{displayName}</div>
                <div className="user-role">{roleLabel} | {countryAccess}</div>
              </div>
            </div>
          </div>
        </div>

        <div className={`app-shell ${isCollapsed ? 'is-sidebar-collapsed' : ''}`}>
          <aside className={`simple-sidebar ${isCollapsed ? 'is-collapsed' : ''}`} aria-label="Left menu">
            <nav
              className="simple-sidebar-nav"
              onClick={suppressSidebarTooltip}
              onMouseOut={releaseSidebarTooltip}
            >
              {!isCollapsed && <h4 className="simple-menu-heading">MAIN</h4>}

              <button
                type="button"
                className={`simple-menu-link simple-menu-button ${!activeThriveSubpage && !activePlaceholderPage ? 'is-active' : ''}`}
                data-tooltip-title="Home"
                data-tooltip="Overview of immunization performance, news, and module shortcuts."
                onClick={() => {
                  setActiveThriveSubpage(null);
                  setActivePlaceholderPage(null);
                }}
              >
                <Home size={18} className="simple-menu-icon" />
                {!isCollapsed && <span className="simple-menu-label">Home</span>}
              </button>
              <button
                type="button"
                className={`simple-menu-link simple-menu-button ${activePlaceholderPage === 'global-coverage' ? 'is-active' : ''}`}
                data-tooltip-title="Global Coverage"
                data-tooltip={sidebarTooltips['global-coverage']}
                onClick={() => {
                  setActiveThriveSubpage(null);
                  setActivePlaceholderPage('global-coverage');
                }}
              >
                <Earth size={18} className="simple-menu-icon" />
                {!isCollapsed && <span className="simple-menu-label">Global Coverage</span>}
              </button>
              <button
                type="button"
                className={`simple-menu-link simple-menu-button ${activePlaceholderPage === 'new-vaccines' ? 'is-active' : ''}`}
                data-tooltip-title="Vaccines Programmes"
                data-tooltip={sidebarTooltips['new-vaccines']}
                onClick={() => {
                  setActiveThriveSubpage(null);
                  setActivePlaceholderPage('new-vaccines');
                }}
              >
                <Syringe size={18} className="simple-menu-icon" />
                {!isCollapsed && <span className="simple-menu-label">Vaccines Programmes</span>}
              </button>
              <button
                type="button"
                className={`simple-menu-link simple-menu-button ${activeThriveSubpage ? 'is-active' : ''}`}
                data-tooltip-title="Vaccine and Cold Chain"
                data-tooltip={sidebarTooltips.thrive360}
                onClick={() => {
                  setActiveThriveSubpage('vccm');
                  setActivePlaceholderPage(null);
                }}
              >
                <LayoutDashboard size={18} className="simple-menu-icon" />
                {!isCollapsed && <span className="simple-menu-label">Vaccine and Cold Chain</span>}
              </button>
              {!isCollapsed && (
                <div className="simple-submenu">
                  {thriveSubpages.map((page) => (
                    <button
                      key={page.key}
                      type="button"
                      className={`simple-submenu-link ${activeThriveSubpage === page.key ? 'is-active' : ''}`}
                      onClick={() => {
                        setActiveThriveSubpage(page.key);
                        setActivePlaceholderPage(null);
                      }}
                    >
                      {page.label}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className={`simple-menu-link simple-menu-button ${activePlaceholderPage === 'demand-vaccine-trust' ? 'is-active' : ''}`}
                data-tooltip-title="Demand & Vaccine Trust"
                data-tooltip={sidebarTooltips['demand-vaccine-trust']}
                onClick={() => {
                  setActiveThriveSubpage(null);
                  setActivePlaceholderPage('demand-vaccine-trust');
                }}
              >
                <HeartHandshake size={18} className="simple-menu-icon" />
                {!isCollapsed && <span className="simple-menu-label">Demand & Vaccine Trust</span>}
              </button>
              <button
                type="button"
                className={`simple-menu-link simple-menu-button ${activePlaceholderPage === 'outbreaks' ? 'is-active' : ''}`}
                data-tooltip-title="Outbreaks"
                data-tooltip={sidebarTooltips.outbreaks}
                onClick={() => {
                  setActiveThriveSubpage(null);
                  setActivePlaceholderPage('outbreaks');
                }}
              >
                <ShieldCheck size={18} className="simple-menu-icon" />
                {!isCollapsed && <span className="simple-menu-label">Outbreaks</span>}
              </button>
              {canAccessBudgets && (
                <button
                  type="button"
                  className={`simple-menu-link simple-menu-button ${activePlaceholderPage === 'budgets' ? 'is-active' : ''}`}
                  data-tooltip-title="Financing"
                  data-tooltip={sidebarTooltips.budgets}
                  onClick={() => {
                    setActiveThriveSubpage(null);
                    setActivePlaceholderPage('budgets');
                  }}
                >
                  <Wallet size={18} className="simple-menu-icon" />
                  {!isCollapsed && <span className="simple-menu-label">Financing</span>}
                </button>
              )}
              {canAccessScip && (
                <button
                  type="button"
                  className={`simple-menu-link simple-menu-button ${activePlaceholderPage === 'scip-tool' || activePlaceholderPage === 'scip-tool-detail' ? 'is-active' : ''}`}
                  data-tooltip-title="Planning and Monitoring"
                  data-tooltip={sidebarTooltips['scip-tool']}
                  onClick={() => {
                    setActiveThriveSubpage(null);
                    setActivePlaceholderPage('scip-tool');
                  }}
                >
                  <ClipboardList size={18} className="simple-menu-icon" />
                  {!isCollapsed && <span className="simple-menu-label">Planning and Monitoring</span>}
                </button>
              )}
              {canAccessManagementKpi && (
                <button
                  type="button"
                  className={`simple-menu-link simple-menu-button ${activePlaceholderPage === 'management-kpi' ? 'is-active' : ''}`}
                  data-tooltip-title="Management KPI"
                  data-tooltip={sidebarTooltips['management-kpi']}
                  onClick={() => {
                    setActiveThriveSubpage(null);
                    setActivePlaceholderPage('management-kpi');
                  }}
                >
                  <Activity size={18} className="simple-menu-icon" />
                  {!isCollapsed && <span className="simple-menu-label">Management KPI</span>}
                </button>
              )}
            </nav>

          </aside>

          <main className="content-shell" aria-label="Home page content">
            {activeThriveSubpage ? (
              <section className="home-section thrive-embed-section">
                <div className="thrive-embed-content">
                  {renderThriveSubpageContent()}
                </div>
              </section>
            ) : selectedPlaceholderPage ? (
              renderPlaceholderPageContent()
            ) : (
              <>
                <section className="home-section coverage-overview">
                  <div className="coverage-heading-row">
                    <div>
                      <h2 className="coverage-title">{selectedHomeCountryLabel}</h2>
                    </div>

                    {homeCountryOptions.length > 0 && (
                      <label className="home-country-picker">
                        Country
                        <select
                          value={selectedHomeCountryIso3}
                          onChange={(event) => setSelectedHomeCountryIso3(event.target.value)}
                          disabled={homeCountryOptions.length === 1}
                        >
                          {homeCountryOptions.length > 1 && (
                            <option value={HOME_COUNTRY_ALL_VALUE}>All assigned countries</option>
                          )}
                          {homeCountryOptions.map((country) => (
                            <option key={country.value} value={country.value}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>

                  <div className="kpi-grid">
                    {homeMainKpiCards.map((card) => {
                      const Icon = card.icon;

                      return (
                        <button
                          key={card.key}
                          type="button"
                          className="kpi-card kpi-action-card"
                          onClick={() => {
                            setActiveThriveSubpage(card.thriveSubpage || null);
                            setActivePlaceholderPage(card.placeholderPage || null);
                          }}
                          aria-label={`Open ${card.label}`}
                        >
                          <div className="kpi-header">
                            <div className={`kpi-icon ${card.iconClassName}`}>
                              <Icon size={14} />
                            </div>
                            <p className="kpi-label">{card.label}</p>
                          </div>
                          {Array.isArray(card.metrics) && card.metrics.length > 1 ? (
                            <div className="kpi-split">
                              {card.metrics.map((metric) => (
                                <div key={metric.meta} className="kpi-split-item">
                                  <p className="kpi-value kpi-action-value">{metric.value}</p>
                                  <p className="kpi-meta">{metric.meta}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <>
                              <p className="kpi-value kpi-action-value">{card.value}</p>
                              <p className="kpi-meta">{card.meta}</p>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="home-section updates-section">
                  <div className="updates-head">
                    <div>
                      <h3 className="updates-title">Global outbreaks news</h3>
                    </div>
                  </div>

                  <div className="updates-grid">
                    {isWhoArticlesLoading && (
                      <article className="update-card">
                        <div className="update-title-row">
                          <h4>Loading WHO updates...</h4>
                        </div>
                        <p>Please wait while outbreak articles are fetched.</p>
                      </article>
                    )}

                    {!isWhoArticlesLoading && whoArticles.length === 0 && (
                      <article className="update-card">
                        <div className="update-title-row">
                          <h4>No WHO updates available</h4>
                        </div>
                        <p>We could not load outbreak articles at this time.</p>
                      </article>
                    )}

                    {!isWhoArticlesLoading && whoArticles.map((article, idx) => (
                      <article key={`who-article-${idx}`} className="update-card">
                        <div className="update-title-row">
                          <h4>{truncateArticleTitle(article.title)}</h4>
                        </div>
                        <p>{createArticlePreview(article.text)}</p>
                        {article.url && (
                          <a
                            className="update-source-link"
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Source: WHO{article.date ? ` | ${article.date}` : ''}
                          </a>
                        )}
                        {!article.url && (
                          <span className="update-source-link">
                            Source: WHO{article.date ? ` | ${article.date}` : ''}
                          </span>
                        )}
                      </article>
                    ))}
                  </div>
                </section>

                <section className="home-section summary-matrix-section">
                  <div className="summary-matrix-head">
                    <div>
                      <h3 className="updates-title">Summary Matrix</h3>
                      <p>Integrated country view across coverage, zero-dose, financing and demand/trust over time.</p>
                    </div>
                    <div className="summary-matrix-legend" aria-label="Summary matrix trend legend">
                      <div className="summary-matrix-filter" aria-label="Filter summary matrix by Gavi country status">
                        <span>Country type</span>
                        <div className="summary-filter-pills">
                          {[
                            { value: 'gavi', label: 'Gavi countries' },
                            { value: 'non-gavi', label: 'Non-Gavi countries' },
                            { value: 'all', label: 'All countries' }
                          ].map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={`summary-filter-pill ${summaryCountryTypeFilter === option.value ? 'is-active' : ''}`}
                              onClick={() => setSummaryCountryTypeFilter(option.value)}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <span><i className="summary-legend-line" />Percent domains</span>
                      <span><i className="summary-legend-bars" />Budget domain</span>
                    </div>
                  </div>

                  <div className="summary-matrix-wrap">
                    <table className="summary-matrix-table" aria-label="Integrated country summary matrix">
                      <thead>
                        <tr>
                          <th scope="col">Country</th>
                          <th scope="col">DTP3 Coverage</th>
                          <th scope="col">Zero-Dose</th>
                          <th scope="col">GAVI Budget</th>
                          <th scope="col">Demand / Trust VCI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isSummaryMatrixLoading && (
                          <tr>
                            <td colSpan="5">Loading integrated country time series...</td>
                          </tr>
                        )}
                        {!isSummaryMatrixLoading && visibleHomeSummaryCountryRows.length === 0 && (
                          <tr>
                            <td colSpan="5">No country time-series data found for this filter.</td>
                          </tr>
                        )}
                        {!isSummaryMatrixLoading && visibleHomeSummaryCountryRows.map((country) => (
                          <tr key={country.key}>
                            <th scope="row">
                              <span className="summary-country-name">{country.countryName}</span>
                              <span className="summary-country-code">
                                {country.iso3} | {country.unicefRo}
                              </span>
                            </th>
                            <td>
                              <SummaryDomainCell
                                countryName={country.countryName}
                                label="DTP3 coverage"
                                series={country.coverageSeries}
                                formatter={formatSummaryPercent}
                                trendFormatter={(value) => `${value > 0 ? '+' : ''}${value.toFixed(1)} pp`}
                                maxValue={100}
                              />
                            </td>
                            <td>
                              <SummaryDomainCell
                                countryName={country.countryName}
                                label="Zero-dose"
                                series={country.zeroDoseSeries}
                                formatter={formatSummaryPercent}
                                trendFormatter={(value) => `${value > 0 ? '+' : ''}${value.toFixed(1)} pp`}
                                maxValue={100}
                              />
                            </td>
                            <td>
                              <SummaryDomainCell
                                countryName={country.countryName}
                                label="GAVI budget"
                                series={country.budgetSeries}
                                formatter={formatBudgetCurrency}
                                trendFormatter={formatBudgetDelta}
                                variant="bars"
                              />
                            </td>
                            <td>
                              <SummaryDomainCell
                                countryName={country.countryName}
                                label="Demand and trust VCI"
                                series={country.vciSeries}
                                formatter={formatSummaryPercent}
                                trendFormatter={(value) => `${value > 0 ? '+' : ''}${value.toFixed(1)} pp`}
                                maxValue={100}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {filteredHomeSummaryCountryRows.length > 12 && (
                    <div className="summary-matrix-footer">
                      <p>
                        Showing {visibleHomeSummaryCountryRows.length} of {filteredHomeSummaryCountryRows.length} countries in this filter.
                      </p>
                      <button
                        type="button"
                        className="vaccines-expand-button"
                        onClick={() => setShowAllSummaryCountries((current) => !current)}
                      >
                        {showAllSummaryCountries ? 'Show fewer countries' : `Show all ${filteredHomeSummaryCountryRows.length} countries`}
                      </button>
                    </div>
                  )}
                </section>
              </>
            )}
          </main>

          <aside
            className={`right-sidebar ${isRightSidebarCollapsed ? 'is-collapsed' : ''}`}
            aria-label="Right sidebar"
            aria-hidden={isRightSidebarCollapsed}
          >
            <div className="right-sidebar-inner">
              <div className="right-sidebar-header">
                <h3 className="right-sidebar-title">My Calendar</h3>
                <button
                  type="button"
                  className="right-sidebar-collapse"
                  onClick={() => setIsRightSidebarCollapsed(true)}
                  aria-label="Collapse right sidebar"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="calendar-card">
                <div className="calendar-nav">
                  <button type="button" className="calendar-arrow" aria-label="Previous month">
                    {'<'}
                  </button>
                  <span className="calendar-month">January 2026</span>
                  <button type="button" className="calendar-arrow" aria-label="Next month">
                    {'>'}
                  </button>
                </div>

                <div className="calendar-grid">
                  <span className="calendar-weekday">28</span>
                  <span className="calendar-weekday">29</span>
                  <span className="calendar-weekday">30</span>
                  <span className="calendar-weekday">31</span>
                  <button type="button" className="calendar-day">1</button>
                  <button type="button" className="calendar-day">2</button>
                  <button type="button" className="calendar-day">3</button>
                  <button type="button" className="calendar-day">4</button>
                  <button type="button" className="calendar-day">5</button>
                  <button type="button" className="calendar-day">6</button>
                  <button type="button" className="calendar-day">7</button>
                  <button type="button" className="calendar-day">8</button>
                  <button type="button" className="calendar-day is-selected">9</button>
                  <button type="button" className="calendar-day">10</button>
                  <button type="button" className="calendar-day">11</button>
                  <button type="button" className="calendar-day">12</button>
                  <button type="button" className="calendar-day">13</button>
                  <button type="button" className="calendar-day">14</button>
                  <button type="button" className="calendar-day">15</button>
                  <button type="button" className="calendar-day">16</button>
                  <button type="button" className="calendar-day">17</button>
                  <button type="button" className="calendar-day">18</button>
                  <button type="button" className="calendar-day">19</button>
                  <button type="button" className="calendar-day">20</button>
                  <button type="button" className="calendar-day">21</button>
                  <button type="button" className="calendar-day">22</button>
                  <button type="button" className="calendar-day">23</button>
                  <button type="button" className="calendar-day">24</button>
                  <button type="button" className="calendar-day">25</button>
                  <button type="button" className="calendar-day">26</button>
                  <button type="button" className="calendar-day">27</button>
                  <button type="button" className="calendar-day">28</button>
                  <button type="button" className="calendar-day">29</button>
                  <button type="button" className="calendar-day">30</button>
                  <button type="button" className="calendar-day">31</button>
                  <span className="calendar-weekday">1</span>
                  <span className="calendar-weekday">2</span>
                  <span className="calendar-weekday">3</span>
                  <span className="calendar-weekday">4</span>
                  <span className="calendar-weekday">5</span>
                  <span className="calendar-weekday">6</span>
                  <span className="calendar-weekday">7</span>
                </div>

                <div className="calendar-event">
                  <div className="calendar-event-date">
                    <div className="event-day">9</div>
                    <div className="event-month">JAN</div>
                  </div>
                  <div className="calendar-event-text">
                    <div className="event-title">Review EPI stock with MOH</div>
                    <div className="event-meta">2:00 PM Regional HQ</div>
                  </div>
                </div>
              </div>

              <div className="right-section">
                <h4 className="right-section-title">My TA Hub requests</h4>
                {isTaLoading && <div className="right-empty-note">Loading TA requests...</div>}
                {!isTaLoading && displayedTaTickets.length === 0 && (
                  <div className="right-empty-note">No requests assigned to you.</div>
                )}
                {!isTaLoading && displayedTaTickets.map((row, idx) => (
                  <div key={`ta-ticket-${idx}`} className="right-check-item">
                    <span>{getTicketTitle(row, idx)}</span>
                    {getTicketMeta(row) && <small className="right-check-meta">{getTicketMeta(row)}</small>}
                    {getTicketPills(row).length > 0 && (
                      <div className="right-check-pills">
                        {getTicketPills(row).map((pill) => (
                          <span key={`${pill.className}-${pill.label}`} className={`right-check-pill ${pill.className}`}>
                            {pill.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default WebV2Module;
